/* Dynamic Plot code copyright 2011 (c) Craig D. Miller */
/**
 * created by cmiller
 */

/**
    @namespace Data manipulation/calculation utilities
*/

import math from 'mathjs'; // use math.fraction() for number <-> fraction string conversions

export const wrapDyndataUtil = function(Dyn) {

    class NiceIntervals {
        constructor( dataMin, dataMax, startingNumIntervals, startingIntervalWidth ) {
            this.min = dataMin;
            this.max = dataMax;
            this.numIntervals = startingNumIntervals;
            this.intervalWidth = startingIntervalWidth;

            this.isAtMin = false;
            this.isAtMax = false;
        }
        setAtMinMax( isAtMin, isAtMax ) {
            this.isAtMin = isAtMin;
            this.isAtMax = isAtMax;
        }
    }

    // class dataType {
    //     constructor( type ) {
    //
    //     }
    // }

    /**
     * Data Type Fraction, for attribute case values and calculations on those values.
     * A fraction is formatted as a string like "2/3", "-1/4", "-100 345/800", but is computed as a number internally.
     */
    class fractionData {

        static approxNumTicksOnAxis = 9; // use a fixed number of ticks on axis and legend for consistency
        static maxNiceDivisor = 500; // if divisor is < 500 (e.g. 1/2, 1/3, ... 1/100, it might be a nice interval for axes.

        /**
         * Extract a fraction from the string, with formatting, if any, and set the result.
         * If not a fraction, return quickly and efficiently and don't change the result.
         */
        static fractionStringToNumber( string, result, isForced ) {
            let isFraction = false,
                isUnsimplified = false, // is unsimplified fraction (e.g. 2/6 not 1/3)
                isImproper = false, // is improper fraction (numerator > denominator, e.g. 4/3 not '1 1/3')
                numericValue, denominator, decimalPlaces;

            // test for ([-])([whole-number])([space])(numerator)/(denominator) pattern in string
            // if not forced, we must have a 1-3 digit numerator/denominator in the string,e.g 1/3 or 123/400
            // if forced, values like "0.5", or "1/2024" are converted to fractions, even though they usually are
            //  considered ordinary numbers (0.5), or month-year ('1/2024'), etc.
            let frac = isForced
                ? Dyn.dataUtil.fractionForcedPattern.exec( string )
                : Dyn.dataUtil.fractionNaturalPattern.exec( string );
            if( frac && frac.length === 6 ) {
                // we have a fraction-like string; test the values
                let sign = (frac[1]==='-') ? -1 : 1, // negative or not
                    whole = Number( frac[2] )||0,  // "123" => -123 (no decimals allowed)
                    spacer = frac[3],
                    numerator = Number( frac[4] ); // "12" => 12 (only non-negative integers allowed)
                denominator = Number( frac[5] ); // "12" => 12 (only positive integers allowed)
                decimalPlaces = frac[5].length; // e.g 1/12 has 2 digits after decimal place
                numericValue = (sign * whole) + (sign * numerator) / denominator;
                if( Number.isFinite( numericValue )) {
                    isFraction = true;
                    isImproper = (numerator > denominator);
                    let commonDivisor = this.gcd( numerator, denominator );
                    isUnsimplified = ( commonDivisor > 1 );
                    Dyn.assert( Number.isInteger(whole), "error in Fraction conversion: "+whole+" whole part of fraction is not an integer" );
                    Dyn.assert( spacer===' '||spacer==='', "error in Fraction conversion: "+spacer+" ! a space");
                    Dyn.assert( Number.isInteger(numerator) && numerator >= 0, "error in Fraction conversion: "+numerator+" numerator is not an integer 0+" );
                    Dyn.assert( Number.isInteger(denominator) && denominator > 0, "error in Fraction conversion: "+numerator+" denominator is not a positive integer" );
                }
                // const d = math.fraction(string)
                // const e = math.number(d)
                // const f = math.format( d, {  fraction: 'ratio' });
                // const g = math.fraction(e);
                // const h = math.format( g, {  fraction: 'ratio' });
                // console.log("fractionStringToNumber", "'"+string+"'", d, e, f, g, h );
                // Dyn.assert( Dyn.mathUtil.isClose( numericValue, e ), "fractionStringToNumber() error for "+string+" : "+numericValue+" != "+e );
            } else if( isForced ) {
                // we have a non-fraction string. Is it a simple number like '0.5' that can be converted to fractions like '1/2'?
                numericValue = Number(string);
                if( Number.isFinite( numericValue )) {
                    let whole = Dyn.mathUtil.floor( numericValue ),
                        fraction = numericValue - whole;
                    decimalPlaces = Dyn.mathUtil.decimalPlaces( fraction );
                    denominator = this.decimalPlacesToDenominator( decimalPlaces );
                    string = string.trim(); // remove any leading/trailing spaces to avoid math.fraction() error
                    isFraction = true;
                    Dyn.assert( Number.isInteger(whole), "error in Fraction conversion: "+whole+" whole part of fraction is not an integer" );
                }
            }
            if( isFraction ) {
                Dyn.assert( Number.isInteger(denominator) && denominator > 0, "error in Fraction conversion: "+denominator+" is not a positive integer" );
                let numMathJS = math.number(math.fraction(string));
                Dyn.assert( Dyn.mathUtil.isClose( numericValue,numMathJS ), "fractionStringToNumber() result: "+numericValue+" does not match MathJS: "+numMathJS);
                result.value = numericValue;
                result.subtype = Dyn.dataUtil.subtypes.kFraction;
                result.formatString = this.setFormatString( denominator, isUnsimplified, isImproper );
                result.fractionDecimals = decimalPlaces;
                // console.log("fractionStringToNumber", string, result.value, result.formatString);
                return numericValue; // success
            }
            return NaN; // not a fraction string
        }

        /** convert the number to a fraction string, at the desired precision, for Dyn.dataUtil.types.kFractions.
         * Reproduces ([-][integer][space])(numerator/denominator) strings parsable by fractionStringToNumber() */
        static numberToFractionString( number, formatString ) {
            let denominator = this.formatStringToDenominator( formatString ),
                wantUnsimplified = Dyn.fractionData.wantUnsimplifiedFractions( formatString ),
                wantImproper = Dyn.fractionData.wantImproperFractions( formatString );
            return numberToParts_mathJS( number, denominator, wantUnsimplified, wantImproper );

            function numberToParts_mathJS( number, denominator, wantUnsimplified, wantImproper ) {
                let f = math.fraction( Dyn.mathUtil.roundIfInteger( number )),
                    w = 0;
                if( !wantImproper ) {
                    w = Math.trunc( f.n / f.d );
                    f.n = f.n % f.d;
                }
                if( wantUnsimplified ) {
                    // if needing unsimplified fraction and desired denom is a multiple of simplified denom
                    // then find the multiple to scale fraction to denominator (e.g. 1/2 to 4/8 if denom == 8)
                    if( (f.d > 0) && (denominator > f.d) && (denominator % f.d === 0) ) {
                        let m = denominator / f.d;
                        f.n *= m;
                        f.d *= m;
                    } else {
                        Dyn.assert( true, "numberToFractionString can't scale"+f.d+" to "+denominator )
                    }
                }
                return numToString( f.s, w, f.n, f.d, wantImproper, wantUnsimplified );
            }

            function numToString( sign, wholeNum, numerator, denominator, wantImproper ) {
                // create strings like "1/2", "1 2/3", "1", "-300", "-3/4", "-4 3/30", etc.
                let hasFraction = (numerator != 0 && denominator != 1) || wantImproper,
                    hasWhole = (wholeNum != 0) || !hasFraction,
                    space = ((hasWhole && hasFraction) ? ' ' : '');
                    //numeratorSign = ((number < 0) && !hasWhole) ? '-' : '';
                let string = (sign===-1 ? '-' : '')
                    + (hasWhole ? wholeNum : '') // whole number part
                    + space // space if both integer and fractional part
                    + (hasFraction ? numerator + '/' + denominator : ''); // fractional part
                //console.log( "'"+string+"'", "from", number, formatString, "with", sign, wholeNum, numerator, denominator, wantImproper ? "I":"P" );
                return string;
            }
        }

        /** create an axis format string
         * @param denominator {number} - e.g. 12 for 7/12 fraction
         * @param isUnsimplified {booean} - Unsimplified or Simplified fraction (3/9 vs 1/2)
         * @param isImproper - Improper or Proper fraction (3/2 vs 1 1/2)
         * @returns {string} - [US][IP]1/{Denom}, e.g. '3/7' => 'SP1/7'
         */
        static setFormatString( denominator, isUnsimplified, isImproper ) {
            return (isUnsimplified?'U':'S') // Unsimplified or Simplified fraction (4/8 v 1/2)
                +(isImproper?'I':'P') // Improper or Proper fraction (4/3 v '1 1/3')
                +'1/'+denominator; // e.g. '1/3', '1/27', etc.
        }

        /** modify a fraction format string to match different number of decimals. */
        static mergeFormat( formatString, decimals, isForcedDecimals ) {
            let newFormat = formatString
            if( isForcedDecimals || !newFormat ) {
                newFormat = this.setFormatString(
                    this.decimalPlacesToDenominator( decimals ),
                    this.wantUnsimplifiedFractions( formatString ),
                    this.wantImproperFractions( formatString ) );
            }
            return newFormat;
        }

        /** modify a fraction format string for use on axes tick marks */
        static getAxisFormat( attribFormatString, axisFormatString ) {
            let newFormat = "";
            if( axisFormatString ) {
                newFormat = axisFormatString;
            } else if( attribFormatString ) {
                newFormat = Dyn.fractionData.setFormatString(
                    this.formatStringToDenominator( attribFormatString ),
                    true, // this.wantUnsimplifiedFractions( attribFormatString ),
                    this.wantImproperFractions( attribFormatString ));
            }
            return newFormat;
        }

        /**
         * Get the best format string for the attribute's case values,
         * given the collected formats found by fractionStringToNumber()
         * @param formatCounts
         * @returns {string}
         */
        static getBestFormatString( formatCounts, numericDecimals ) {
            /* Pick the common denominator,
                e.g. if 1/3, 1/4, 1/12 formats, we pick 1/12.
                e.g. if 1/3 and 1/5 formats, we pick 1/15.
                if a mix of simplified (S) and unsimplified (U) fractions, pick unsimplified, e.g. 1/2 + 2/4 => use 2/4
                if a mix of proper (P) and improper (I) fractions, pick improper, e.g 1 1/2, 3/2 => use 3/2
             */
            const formatArray = Object.entries( formatCounts );
            let bestFormatStr = "",
                bestDenom = 1,
                isUnsimplified = false,
                isImproper = false;
            for( const [ formatStr, count ] of formatArray ) {
                let d = this.formatStringToDenominator( formatStr ),
                    gcd = this.gcd(d,bestDenom);
                isUnsimplified = isUnsimplified || this.wantUnsimplifiedFractions( formatStr );
                isImproper = isImproper || this.wantImproperFractions( formatStr );
                if( bestDenom === 1 ) {
                    bestDenom = d;
                    bestFormatStr = formatStr;
                } else if( d%bestDenom != 0 || d>bestDenom ) {
                    bestDenom *= d/gcd;
                    bestFormatStr = this.setFormatString( bestDenom, isUnsimplified, isImproper );
                } else {
                    bestFormatStr = this.setFormatString( bestDenom, isUnsimplified, isImproper );
                }
            }
            // console.log( "fractionData.getBestFormatString() best=", bestFormatStr, formatCounts );
            if( ! bestFormatStr ) {
                bestDenom = this.decimalPlacesToDenominator( numericDecimals );
                bestFormatStr = this.setFormatString( bestDenom );
                // console.log( "fractionData.getBestFormatString() NO STRING new format=", bestFormatStr, formatCounts );
            }
            return bestFormatStr;
        }


        /**
         * Convert the number of decimal places to a denominator, e.g. 1, 10, 100, 1000,
         * So that we simulate Excel 1/10, 1/100, 1/1000 formatting and rounding
         * @param decimals
         * @returns {number}
         */
        static decimalPlacesToDenominator( decimals ) {
            let digits = Dyn.mathUtil.clipToMinMax( 1, 3, decimals );
            return Math.pow(10, digits);
        }

        /** convert a formatString to the denominator of a fraction, if in the right format.
         * '1/3' => 3, '1/1000' => 1000.  Defaults to 1 if no format or invalid format.
         * inverse of setFormatString()
         * @param formatString
         * @returns {number} - positive integer
         */
        static formatStringToDenominator( formatString ) {
            let denomString = /^..1\/([0-9]+)/.exec( formatString ),
                denomFromFormat = Number( denomString && denomString[1]);
            return( denomFromFormat >= 1 ) ? denomFromFormat : 1;
        }

        /** format the fraction as Unsimplified or Simplified fraction (4/8 v 1/2)? See also setFormatString() */
        static wantUnsimplifiedFractions( formatString ) {
            Dyn.assert( /[US].+/.test(formatString));
            return formatString[0] === 'U';
        }

        /** format the fraction as improper, or a proper fraction (3/2 vs 1 1/2)? See also setFormatString() */
        static wantImproperFractions( formatString ) {
            Dyn.assert( /.[IP].+/.test(formatString));
            return formatString[1] === 'I';
        }

        /**
         * Find the Greatest Common Divisor to simplify the fraction.
         * This the Binary GCD, a.k.a. Stein's Algorithm
         * https://www.geeksforgeeks.org/steins-algorithm-for-finding-gcd/
         * https://en.wikipedia.org/wiki/Binary_GCD_algorithm
         * @param a - non-negative integer
         * @param b - non-negative integer
         */
        static gcd( a,  b) {
            Dyn.assert( Number.isInteger(a) && a >=0 );
            Dyn.assert( Number.isInteger(b) && b >=0 );
            // GCD(0, b) == b; GCD(a, 0) == a, GCD(0, 0) == 0
            if (a == 0)
                return b;
            if (b == 0)
                return a;

            // Finding K, where K is the greatest power of 2 that divides both a and b.
            {   /* eslint-disable no-bitwise */
                let k;
                for( k = 0; ((a | b) & 1) == 0; ++k ) {
                    a >>= 1;
                    b >>= 1;
                }

                /* Dividing a by 2 until a becomes odd */
                while( (a & 1) == 0 )
                    a >>= 1;

                /* From here on, 'a' is always odd. */
                do {
                    /* If b is even, remove all factor of 2 in b */
                    while( (b & 1) == 0 )
                        b >>= 1;

                    /* Now a and b are both odd.
                       Swap if necessary so a <= b,
                       then set b = b - a (which is even).*/
                    if( a > b ) {
                        let t = a;
                        a = b;
                        b = t;
                    }
                    b = (b - a);
                } while( b != 0 );

                /* restore common factors of 2 */
                return a << k;
            }   /* eslint-enable no-bitwise */
        }
    }
    Dyn.fractionData = fractionData;

/** class dataUtil with static data and methods */
class dataUtil {

    static kNumericMissingValue = Math.NaN;	// useful for missing values used in numeric calculations
    static kMissingValue = null;			// general missing value (see getDataType() for comprehensive list)
    static kStringMissingValue = "-";			// treat this string as a missing value rather than a category value
    static kMissingValueName = "{missing}";   // name used to describe missing values, as in 'Gender={missing}'

    static types = {		// Dyn.Attribute.dataType enumerated types
        kCategorical: "categorical",
        kNumerical: "numerical"
        // there is no "missing" data type, because these types only apply to non-missing/non-blank data.
        // The Attribute.isMissing() and dataUtil.isMissing() functions are to determine if there is anything to plot.
    };
    static subtypes = {	// Dyn.Attribute.subType enumerated types
        kDateTime: "datetime",			// date and time, e.g. "Mon Jun 08 2015 13:28:01.0000"
        kDate: "date",  				// date with no time, e.g. "Mon Jun 08 2015"
        kTime: "time",					// time with no date, e.g. "13:28:01.0000"
        kDurationHMS: "duration", 		// duration hours:minutes:seconds - 3 part time without AM/PM, e.g. "13:28:05", can be <0 or >24hours, can have decimal in minutes
        kDurationHM: "durationHM", 		// duration hours:minutes - 2 time without AM/PM, e.g. "13:28" or "-1:23", can be <0 or >24hours, can have decimal in minutes
        kMonth: "month",				// name of month, no year, e.g. "June"
        kMonthYear: 'monthYear',        // name of month with year, e.g. "June 2022"
        kYear: "year",					// 4 digit year (yyyy), e.g. 1984
        kYearMonth: "yearMonth",        // YYYY-MM format, e.g. 1984-01 or 1984-1 (Jan, 1984)
        kDayOfWeek: "dayOfWeek",		// day of week only, e.g. "Monday"
        kExtractedNumber: "extractedNumber",	// extracted single number, e.g. 12 of "12 inches"
        kNumericIntervals: "numericIntervals", // numbers converted to fixed-width intervals, e.g. "10-14", "15-19", "20-24"
        kFraction: 'fraction', // numbers with fractions, e.g. "1 1/2", "-3/400", etc.
    };
    static yearMonthOutputFormats = [ "", "YYYY-MM", "YYYY-Mmm", "MM-YYYY", "Mmm-YYYY" ];
    static getTypesArray() { return Object.values( this.types ); }
    static getSubtypesArray() { return Object.values( this.subtypes ); }
    static isSubtypeValidForType( type, subtype ) {
        let isCatSubtype = (subtype === this.subtypes.kNumericIntervals), // only one valid subtype for cat
            isCatType = (type === this.types.kCategorical),
            isTypeDefined = !!type,
            isSubtypeDefined = !!subtype;
        return ( isTypeDefined && isSubtypeDefined ) ?
            (isCatType ? isCatSubtype : ! isCatSubtype ): // subtype matches type?
            true; // ok if type undefined (determine type dynamically) or OK if subtype undefined (no subtype)
    }

    static kSecondInMS = 1000;
    static kMinuteInMS = 1000 * 60;
    static kHourInMS = 1000 * 60 * 60;
    static kDayInMS = 1000 * 60 * 60 * 24;
    static kYearInMS = 1000 * 60 * 60 * 24 * 365.24; // approximate

    // date/time constants
    static kTodaysDateString = new Date(Date.now()).toDateString();  // e.g. "Mon Jun 08 2015"
    static kThisYearString = new Date(Date.now()).getFullYear().toString(); // e.g. "2015"
    static kThisSundayDateMS = undefined; // see later post-class definition from calcThisSundayDateMS()
    static calcThisSundayDateMS() {
        let sundayMS = this.kDayInMS*(new Date(Date.now()).getDay()),
            todayMS = Date.now() - sundayMS,
            sundayDateString = new Date( todayMS ).toDateString(); // e.g. "Sun Jun 07 2015"
            return Date.parse( sundayDateString );
        }

    // patterns for date/time recognition
    static timePattern = /[0-9]+:[0-9]+/; // number:number
    static time24HMS_Pattern = /^(2[0-3]|[01]?[0-9]):([0-5]?[0-9]):([0-5]?[0-9])$/i; // 00:00:00 - 24:60:60
    // Duration formats: (+/-hours:minutes:seconds.decimals) or (+/-hours:minutes.decimals)
    // hours can be any positive or negative integer
    // minutes must be 0-59
    // seconds must be 0-59
    // TODO: tight up duration patterns to allow '-100:01:23.4567' but not '-100:01:23467'
    static durationHMSPattern = /^([-]?)([0-9]*):([0-5][0-9]):([0-5][0-9][.]?[0-9]*)$/; // durations (+/-hours:minutes:seconds) with 2 colons e.g. 00:00:00, 1000:00:00, -50:00:30, 0:00:00
    static durationHMPattern =  /^([-]?)([0-9]*):([0-5][0-9][.]?[0-9]*)$/; // durations (+/-hours:minutes) with 1 colon e.g. 00:00, 1000:00, -50:00, 10:12, 1:12, 10:12.345, but not integers 12, -12, etc.
    static datePattern = /[\d\w]+[,]*(\s|-|\/)[\d\w]+/; // (letters/digits)[,](space or dash or slash)(letters/digits), e.g. "2015 Jan", "01, 15", "1/15", etc.
    static datePatternISO = /[\d]+-[\d]+/; // ISO dates have dashes: look for (digits)(dash)(digits), e.g. "2015-01", "2000-05-25", etc.
    static datePatternNonISO = /[\w]+(\s|\/)[\d]+[,]*(\s|-|\/)[\d]/; // non-iso date: (word)(space or slash)(digits)[,](space or slash)(digits)
    static monthPattern = /^(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)/i;
    static monthYearPattern = /^([\w]+)([, \-/]{1,2})([\d]+)/i; // 'abcd'[1+ letter] then [,- ] then 12345[1+ digit], e.g. 'March-2022'
    static monthYearDigitsPattern = /^([0-9]{1,2})([, \-/]{1,2})([\d]+)/; // "1/1987" or "02/2345" or 3-2000
    static monthYearSeparators = [' ',', ','-','/']; // 'Jan 1234' or 'Jan, 1234' or 'Jan-1234' or 'Jan/1234'
    static dayOfWeekPattern = /^(sun|mon|tue|wed|thu|fri|sat)/i;
    static yearPattern = /^[12][0-9]{3}$/; // Year 1000-2999
    static yearPatternForced = /^([0-9]{3,4})[/D]?.*/; // Year 000-9999, followed by any trailing data
    static yearMonthDigitsPattern = /^([12][0-9]{3})[/-]([\d]{1,2})$/; // YYYY-MM, year 1000-2999, - or / separator, 1-or-2 digit month.
    static yearMonthNamePattern = /^([\d]{1,4})([, \-/]{1,2})([\w]+)$/; // YYYY-MMM+ with english name for month, 3+ letters
    static yearMonthForcedPattern = /^([12][0-9]{3})[/-]([\d]{1,2})[/D]?.*/; // like YYYY-MM but ignoring trailing data (YYYY-MM-DD, etc).
    static kDateTimeZoneCorrection = Date.parse("1/1/1970"); // millisecond difference from GMT to local time zone
    static kDateTimeNeedsISODateCorrection = (!/2014/.test(new Date(Date.parse("2014-01-01T00:00:00")).toLocaleDateString())); // true if (new Date(Date.parse("2014-01-01"))==="2013-12-31...")
    static kDateOnlyNeedsISODateCorrection = (!/2014/.test(new Date(Date.parse("2014")).toLocaleDateString())); // true if (new Date(Date.parse("2014-01-01"))==="2013-12-31...")

    static kExtractedNumberNaturalPattern = /^[^\d\-]*[-]?[0-9]{1,3}(,[0-9]{3})*(\.[0-9]+)?[^\d\-]*$/; // Mandatory integer and optional fraction, no exponents. Decimal dot must be omitted if the fraction is omitted. From Regular Expressions Cookbook page 399 section 6.11
    static kExtractedNumberForcedPattern = /([^\d\-\+]*)([-+]?\b[0-9]+[\.]?[0-9]*)([eE][-+]?[0-9]+\b)?(.*)/; // number in larger body of text, no commas, optional exponent, from Regular Expressions Cookbook, page 397, section 6.10
    static kEmbeddedNumberWithCommasPattern = /\d{1,3},\d{3}/; // pattern D,DDD which is the minimum necessary set of characters for valid use of a comma in a number. E.g. 1,234 matches
    static fractionNaturalPattern = /^([-]?)([0-9]*\b)([ ]?)([0-9]{1,3})\/([0-9]{1,3}$)/; // fraction, with optional negative sign, optional whole-number part
    static fractionForcedPattern = /^([-]?)([0-9]*\b)([ ]?)([0-9]+)\/([0-9]+)$/; // fraction, with optional negative sign, optional whole-number part


    // for input matching with first 3 characters of lower case string, after matching pattern above, to get index
    static kMonthsOfYearShortLC = ["jan","feb","mar","apr","may","jun","jul","aug","sep","oct","nov","dec"];
    static kMonthsOfYearLongLC = ["january","february","march","april","may","june","july","august","september","october","november","december"];
    static kDaysOfWeekShortLC = ['sun','mon','tue','wed','thu','fri','sat'];
    static kDaysOfWeekLongLC = ['sunday','monday','tuesday','wednesday','thursday','friday','saturday'];
    // for output to string, converting index to a display value
    static kDisplayMonths = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    static kDisplayDays = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

    static isString( str ) {
        return typeof str === 'string';
    }

    static isNonEmptyString( str ) {
        return (typeof str === 'string' ) && (str.length > 0);
    }

    /**
     * Copy the row-major table rows into new column-major table columns.
     * @param tableRows {Array[]} - [numRows][numColumns] array of arrays of cell values.
     * @param columnIds {[]} - ID strings for columns
     * @return {[]|null} - [numColumns][numRows] array of arrays of cell values.
     */
    static copyRecordListToColumnArrays( tableRows, columnIds ) {
        // check for empty table
        if (!tableRows || !columnIds) {
            return [];  // return 0 rows and 0 columns
        }

        // create the columns
        let numCells = columnIds.length,
            numRows = tableRows.length,
            columnArray = new Array( numCells );
        for( let cellIndex = 0; cellIndex < numCells; cellIndex++) {
            columnArray[ cellIndex ] = new Array( numRows );
        }
        if( tableRows.length === 0 ) {
            return columnArray; // return 1+ columns with 0 rows each
        }

        // copy and transpose cell data, and normalize convert to category (string), finite number, or missing value (null)
        for( let rowIndex = 0, numRows = tableRows.length; rowIndex < numRows; rowIndex++) {
            for( let cellIndex = 0; cellIndex < numCells; cellIndex++) {
                //let colText = Dyn.dataUtil.normalizeCaseValueString( tableRows[rowIndex][cellIndex] );
                columnArray[ cellIndex ][ rowIndex ] = tableRows[rowIndex][cellIndex];
            }
        }
        return columnArray;
    }

    /**
     * Given an array of column arrays,
     * define an attribute with the name and the remainder
     * of elements as the Attribute case data.
     * NOTE: Modifies the input array (removes the header row).
     * @param column_names
     * @param arrayOfColumns
     * @param column_IDs
     * @param metaData
     * @return {*[]|*} - array of Attribute objects
     */
    static copyColumnArraysToAttribs( column_names, arrayOfColumns=[], column_IDs, metaData ) {
        var attribs = new Array( arrayOfColumns.length ),
            attribOptions = metaData && metaData.fields || []; // array of attribute options or empty array
        // for each column in table
        for( let c = 0, cc = arrayOfColumns.length; c < cc; c++) {
            let cases = arrayOfColumns[c],
                name = column_names[c],
                id = column_IDs[c],
                options = attribOptions[c];
            if(options) {
                Dyn.assert( id === options.id, "column_id '"+id+"' mismatched with metadata attribute id '"+options.id+"'");
            }
            attribs[c] = new Dyn.Attribute( name, cases, c, cc, id, options );
        }
        return attribs;
    };

    /**
     * Get the raw case data from dataset, stored in row-major
     * order (like a table), excluding formula values.
     * @param attribArray
     * @param numCases
     * @returns {*[]}
     */
    static getTableRowDataRaw( dataSet ) {
        const mv = Dyn.dataUtil.kMissingValue;
        let rows = [];
        let headerRow = [];
        dataSet.attribs.forEach((attrib)=>{
            if(!attrib.isCaseAttribute) {
                headerRow.push( attrib.name );
            }
        });
        rows.push(headerRow);
        for(let i=0, numRows = dataSet.getNumCasesPreFilter(); i<numRows; i++) {
            // add a row
            let rowData = [];
            dataSet.attribs.forEach((attrib)=>{
                if(!attrib.isCaseAttribute) {
                    rowData.push( attrib.isDerived ? mv : attrib.originalCases[i] );
                }
            })
            rows.push(rowData);
            // Note: above way is 3x faster than this call:
            // rows.push(dataSet.getTableRowData( i, { rawNumbers:true, formatted:false, noDerivedData:true }));
        }
        return rows;
    }

    /**
     * create a new attribute suitable for adding to the dataset, e.g. a formula attribute
     * to be calculated by the caller.
     * @param id
     * @param name
     * @param cases
     * @param metaData
     * @returns {Dyn.Attribute}
     */
    // MOVED to dataSet.js : MetaData.newAttributeFromMetaData()
    // static newAttributeFromMetaData( id, name, cases, metaData ) {
    //     let attributeIndex = 0, // caller should update later
    //         attributeCount = 1; // caller should update later
    //     return new Dyn.Attribute( name, cases, attributeIndex, attributeCount, id, metaData );
    // }

    // MOVED to dataSet.js : MetaData.newMetaDataFieldFromName()
    // static newMetaDataFieldFromName( attribIndex, name, longName, formulaString ) {
    //     // consistently construct a metaDataField for a dataSet, with optional parameters
    //     let attribName = name || 'attrib',
    //         attribId = Dyn.plotUtil.slugify( attribName ) + "-attrib" + attribIndex,
    //         metaData = {
    //             "sort": "ascending",
    //             "name": attribName,
    //             "long_name": longName || attribName, // field.long_name maps to attribute.longName
    //             "id": attribId
    //         };
    //     if( formulaString ) {
    //         metaData.attribute_type = "derived";
    //         metaData.formula = formulaString;
    //     }
    //     return metaData;
    // }

    /**
     * Return true if this is a valid attribute name.
     * All places where an attribute is created should use this centralized method for consistency.
     */
    static isValidAttributeName( name ) {
        // for now, we require strings of any length 1+,
        // any character is valid.
        return (typeof name === 'string') && name.length > 0;
    }

    /**
     * Is the given attribute the auto-generated "Case" attribute, which is
     * hidden in the case card but visible in the case table?
     * This utility gives a consistent way to identify it.
     * @param attribute {Dyn.Attribute}
     * @returns {boolean}
     */
    static isSpecialAttribute_Case( attribute ) {
        return attribute && attribute.id === 'id';
    }

    static testTable( /*tableID*/ ) {
        // Make sures we have tables.
        var tables = document.getElementsByTagName( "table" );
        if( tables.length < 1 ) {
            throw new Error( "This document contains " + tables.length + " tables" );
        }
    }

    /** return true if value is a finite number; to ensure data values are
     *      finite-numeric, non-empty-string, or missing */
    static isNumber( caseValue ) {
        return (typeof caseValue === "number" && isFinite( caseValue ));
    }

    /** return true if data value is missing; caller should use normalizeCaseValueString() to ensure data values are
     *      finite-numeric, non-empty-string, or missing */
    static isMissing( caseValue ) {
        Dyn.assert( caseValue === Dyn.dataUtil.kMissingValue
            || Dyn.dataUtil.isNumber( caseValue )
            || (typeof caseValue === 'string' && caseValue.length > 0), "value not one of the types returned by normalizeCaseValueString()" );
        return (caseValue === Dyn.dataUtil.kMissingValue);
    }

    static extractedNumberParam = '{{n}}';

    /**
     * Convert a string to a numeric value, extracting valid any single valid number from the string if necessary.
     * Finds both integers " -2 " and floating point " -2.3 feet " and exponential values "almost -2.3e4".
     * @param {string} string -- data value to convert to a number
     * @param {*} [oFormat] -- optional object to store numeric value extracted from string
     * @param {string} [oFormat.subtype] -- result: string in enum Dyn.dataUtil.subtypes
     * @param {number} [oFormat.value] -- result: numeric value (again)
     * @returns {number|NaN} -- finite numeric value, or NaN a valid number cannot be found.
     */
    static stringExtractNumber( string, oFormat, isForced ) {
        var subString = string,
            hasNumber = false,
            value, formatString, trailingZerosCount;

        // step 0: check for natural extracted number, or forced numeric
        if( !( Dyn.dataUtil.kExtractedNumberNaturalPattern.test( string ) || isForced )) {
            return NaN;
        }

        // step 1: if commas found in between a numeric substring, remove commas ("-1,234,567.0" => "-1234567.0", "us$10,000"=>"us$10000")
        if( Dyn.dataUtil.kEmbeddedNumberWithCommasPattern.test( string ) ) {
            subString = string.replace( /,/g, '' );
        }

        // step 2: find the first number in the string, ignore everything else ("-223.5 miles"=>"-223.5", "high 5"=>"5", "10/20/30"=>"10", "us$10000"=>"10000", "-10e7ms"=>"-10e7")
        subString = Dyn.dataUtil.kExtractedNumberForcedPattern.exec( subString );

        // step 3: if a numeric substring was found, convert to a numeric value
        if( subString !== null ) {
            let parsableString = subString[2]+(subString[3]||''); // number (-12.34) plus optional exponent (e-123)
            trailingZerosCount = this.getDecimalCountWithTrailingZeros( parsableString );
            value = parseFloat( parsableString ); // convert substring to number
            hasNumber = isFinite( value );	  // make sure it is really a number
            formatString = subString[1]+this.extractedNumberParam+subString[4];
            //console.log( "stringExtractNumber extracted "+value+" from '"+string+"'", "parsed:"+parsableString, "format:'"+formatString+"'");
        }
        if( hasNumber && oFormat ) { // save with optional subtype format
            oFormat.subtype = Dyn.dataUtil.subtypes.kExtractedNumber;
            oFormat.value = value;
            if( formatString !== this.extractedNumberParam ) {
                oFormat.formatString = formatString;
            }
            if( trailingZerosCount ) {
                oFormat.trailingZerosCount = trailingZerosCount;
            }
        }
        return hasNumber ? value : NaN;
    }

    /**
     * count the number of decimals to the right of the decimal point, that end in a zero.
     * e.g. "-12.450" => 3, "12.3" => 0.
     * @param string
     * @returns {number}
     */
    static getDecimalCountWithTrailingZeros( string ) {
        let trailingZerosCount = 0,
            subString = /\.([\d]*[0]+)/.exec( string );
        if( subString?.length > 0 ) {
            trailingZerosCount = subString[1].length;
        }
        //console.log("getDecimalCountWithTrailingZeros", string, subString, trailingZerosCount);
        return trailingZerosCount;
    }

/**
 * Convert a string to a DateTime value, which is stored as the number of milliseconds since 1970.
 * NOTE: we convert dates to and from the correct local time, if no time zone specifier is in the string.
 * and auto-adjust for ISO-format dates which sometimes are treated as UTC/GMT in some browsers
 * @param {string} string -- data value to convert to a dateTime number
 * @param {*} [oFormat] -- optional object to store datetime value extracted from string
 * @param {boolean} [oFormat.isDateTime] -- result: true if this is a valid date/time value
 * @param {string} [oFormat.subtype] -- result: string in enum Dyn.dataUtil.subtypes
 * @param {number} [oFormat.value] -- result: Date() value in milliseconds since 1970/01/01 00:00 UT.
 * @param [forcedSubType] {string} -- optional date-time to force data to, if any
 * @returns {number|undefined} -- date/time in number of milliseconds between the date and January 1, 1970, or undefined if not a valid date.
 */
static stringToDateTime( string, oFormat, forcedSubType ) {

	function getDateTimeFromString( string, result ) {
        // initial test using Date.parse(),
        // then remove browser inconsistencies using removeFalseDateTime()
        result.dateMS = Date.parse( string );
        result.isDateTime = Number.isFinite( result.dateMS );
        if( result.isDateTime )
            util.removeFalseDateTime( string, result ); // remove common non-date value

        // see if it is a Date, Date+Time or scalar value
        if( result.isDateTime && oFormat ) {
            let hasTime = util.timePattern.test( string ),
                hasDate = util.datePattern.test( string );
            if( hasTime && hasDate ) {
                if( util.kDateTimeNeedsISODateCorrection && util.datePatternISO.test( string )) {
                    result.dateMS += util.kDateTimeZoneCorrection; // ISO-format date-time needs timezone correction in Safari, not Chrome or FireFox.
                }
                result.dateTimeSubtype = subtypes.kDateTime;
            } else if( hasDate ) {
                if( util.kDateOnlyNeedsISODateCorrection && util.datePatternISO.test( string )) {
                    result.dateMS += util.kDateTimeZoneCorrection; // ISO-format date-only needs timezone correction in Safari + FireFox, not Chrome.
                }
                result.dateTimeSubtype = subtypes.kDate;
            } else if( Dyn.mathUtil.isNumber( string )) {
                // scalar numbers are interpreted as dates
                result.dateTimeSubtype = subtypes.kDate;
            } else {
                // Date.parse() claims it is a date or time but no match for date or time pattern
                // therefore mark as "not a date" and return.
                result.isDateTime = false;
                console.log( "Dyn.dataUtil.stringToDateTime() could not find date or time in '"+string+"', add this pattern to Dyn.dataUtil.removeFalseDateTime()");
            }
        }
	}
    // function hasDateResult( result ) {
    //     return result.dateTimeSubtype === subtypes.kDateTime
    //         || result.dateTimeSubtype === subtypes.kDate;
    // }
    function getTimeFromString( string, result ) {
        if( util.timePattern.test( string )) {
            // if string might be a time, add today's date to string and test again
            getDateTimeFromString( util.kTodaysDateString + " " + string, result ); // test for time-only string, e.g.  "12:30"
            result.dateTimeSubtype = subtypes.kTime;
        }
    }
    function getDurationFromString( string, result ) {
        // test for partial dates or partial times
        let d = util.durationHMSPattern.exec( string ) || util.durationHMPattern.exec( string );
        if( d ) {
            // convert duration in HHHH:MM:SS or MMMM:SS format to an integer number of seconds (NOT milliseconds)
            Dyn.assert( 4 <= d.length && d.length <= 5, "invalid duration format, correct pattern:" + string );
            result.isDateTime = true;
            let sign = (d[1] === '-') ? -1 : 1;
            if( d.length === 5 ) {
                result.dateMS = sign * ((parseInt( d[2] ) * 3600) + (parseInt( d[3] ) * 60) + Number( d[4] )); // e.g. 23:59:59 or -100:55:23 to number seconds
                result.dateTimeSubtype = subtypes.kDurationHMS;
            } else {
                result.dateMS = sign * ((parseInt( d[2] ) * 3600) + Number( d[3] * 60 )); // e.g. -100:55 or 59:59 HH:MM to number of seconds
                result.dateTimeSubtype = subtypes.kDurationHM;
            }
            //console.log( result.dateTimeSubtype, result.dateMS, this.durationSerialToDurationString( result.dateMS, result.dateTimeSubtype ), " from ", string );
        }
    }
    function getMonthOrMonthYearFromString( string, result, forcedSubType ) {
        let isMonthOnly=false,
            isMonthYear=false,
            yearIndex, monthIndex;
        // NOTE: we don't have monthPatternForced or monthYearPatternForced yet, so we ignore 'forcedSubType' parameter.
        //     isMonthOnly =( forcedSubType === subtypes.kMonth );
        //     isMonthYear =( forcedSubType === subtypes.kMonthYear );

        if( util.monthPattern.test( string )) {
            // string starts with 3 letters of a month ("jan..."), is it a month or Month-Year?
            let msy = util.monthYearPattern.exec( string );
            if( msy && msy.length === 4 ) {
                // test for month-year with alpha month, e.g. "Jan-2022"
                let monthStr=msy[1], separatorStr=msy[2], yearStr=msy[3],
                    isValidSeparator = util.monthYearSeparators.includes( separatorStr );
                monthIndex = util._getMonthIndexFromString( monthStr );
                yearIndex = Number( yearStr );
                if( isValidSeparator && monthIndex >= 0 && Number.isFinite( yearIndex )) {
                    isMonthYear = true;
                    result.formatString = "Mmm-YYYY";
                }
            }
            if( ! isMonthYear ) {
                // test for month-only with alpha month, e.g. ("January")
                monthIndex = util._getMonthIndexFromString( string );
                if( monthIndex >= 0 ) {
                    isMonthOnly = true;
                    result.formatString = "Mmm";
                }
            }
        } else {
            // test for "01/1995" month-year as all digits
            let dsy = util.monthYearDigitsPattern.exec(string );
            if( dsy && dsy.length === 4 ) {
                let monthStr=dsy[1], separatorStr=dsy[2], yearStr=dsy[3],
                    isValidSeparator = util.monthYearSeparators.includes( separatorStr );
                monthIndex = Number( monthStr );
                yearIndex = Number( yearStr );
                if( isValidSeparator && (monthIndex >= 1 && monthIndex <= 12) && Number.isFinite(yearIndex )) {
                    monthIndex -= 1; // 1-12 to 0-11
                    isMonthYear = true;
                    result.formatString = "MM-YYYY";
                }
            }
        }
        if( isMonthYear ) {
            result.dateMS = monthIndex + (12 * yearIndex);  // months since Jan,0000
            result.isDateTime = true;
            result.dateTimeSubtype = subtypes.kMonthYear;
            Dyn.assert( monthIndex >= 0 && monthIndex <= 11, "error in Month conversion <"+monthIndex+">" );
            Dyn.assert( Dyn.mathUtil.isInteger( yearIndex ), "error in Year conversion <"+yearIndex+">" );
        } else if( isMonthOnly ) {
            result.dateMS = monthIndex; // 0-11
            result.isDateTime = true;
            result.dateTimeSubtype = subtypes.kMonth;
            Dyn.assert( result.dateMS >= 0 && result.dateMS <= 11 );
        }
    }
    function getYearMonthFromString( string, result, isForced ) {
        let isYM=false,
            yearIndex, monthIndex, formatString;

        // test for year-month YYYY-MM (DIGITS for month) pattern in string
        let ym = util.yearMonthDigitsPattern.exec( string );
        if( isForced && !ym ) {
            // if forced, try year-month-whatever into string (ignore trailing characters if forced)
            ym = util.yearMonthForcedPattern.exec( string );
        }
        if( ym && ym.length === 3 ) { // substrings to year
            yearIndex = Number( ym[1] );  // "2001" => 2001
            monthIndex = Number( ym[2] ) - 1; // "Jan" => 01; range [0-11]
            isYM = Number.isFinite( yearIndex ) && (monthIndex >= 0 && monthIndex <= 11);
            formatString = "YYYY-MM";
        }
        // else test for year-month YYYY-MMM+ (NAME of month) pattern in string
        if( !isYM ) {
            ym = util.yearMonthNamePattern.exec( string );
            if( ym && ym.length === 4 ) {
                yearIndex = Number( ym[1] );
                let isValidSeparator = util.monthYearSeparators.includes( ym[2] );
                monthIndex = util._getMonthIndexFromString( ym[3] );
                isYM = Number.isFinite( yearIndex ) && (monthIndex >= 0 && monthIndex <= 11) && isValidSeparator;
                formatString = "YYYY-Mmm";
            }
        }
        // if success
        if( isYM ) {
            result.isDateTime = true;
            result.dateMS = monthIndex + (12 * yearIndex); // months since Jan,0000
            result.dateTimeSubtype = subtypes.kYearMonth;
            result.formatString = formatString;
            Dyn.assert( monthIndex >= 0 && monthIndex <= 11, "error in Month conversion <"+monthIndex+">" );
            Dyn.assert( Dyn.mathUtil.isInteger( yearIndex ), "error in Year conversion <"+yearIndex+">" );
        }
    }
    function getYearFromString( string, result, isForced ) {
        // try natural conversion first
        let isYear = util.yearPattern.test( string ),
            year = Number( string );
        if( isForced && ! isYear ) {
            // force convert year+ ('1983-12-23-etc') to year?
            let yp = util.yearPatternForced.exec( string );
            isYear = yp && yp.length === 2;
            if( isYear ) {
                year = Number( yp[1] );
            }
        }
        if( isYear ) {
            result.isDateTime = Number.isFinite( year );
            if( result.isDateTime ) {
                result.dateMS = year;
                result.dateTimeSubtype = subtypes.kYear;
            }
        }
    }
    function getDayOfWeekFromString( string, result, isForced ) {
        // NOTE: we don't have a dayOfWeekPatternForced yet, so we ignore 'isForced' parameter.
        let dayIndex = -1;
        if( util.dayOfWeekPattern.test( string )) {
            // if string might be a day-of-week, test again more precisely
            dayIndex = util._getDayOfWeekIndexFromString( string );
        }
        if( dayIndex >= 0  && dayIndex <= 6 ) {
            // getDateTimeFromSerialNumber( util.kThisSundayDateMS + (util.kDayInMS * index ), result );
            result.dateMS = dayIndex; // 0-6
            result.isDateTime = true;
            result.dateTimeSubtype = subtypes.kDayOfWeek;
        }
    }

	var util = Dyn.dataUtil,
		subtypes = util.subtypes,
		result = {};

    // test for standard, ISO Date-Time, Date-only string (Date.parse() tests)
    if( ! result.isDateTime )
	    getDateTimeFromString( string, result ); // test for date-time or date-only

    // test for duration "01:59" or "01:02:59" (no AM or PM)
    if( ! result.isDateTime )
        getDurationFromString( string, result );

    // test for time-only string "12:30 AM". NOTE: test AFTER duration
    if( ! result.isDateTime )
        getTimeFromString( string, result );

    // test for Sun, Mon, etc
    let isForcedDay = (forcedSubType===subtypes.kDayOfWeek);
    if( isForcedDay || ! result.isDateTime )
        getDayOfWeekFromString( string, result, isForcedDay );

    // test for "January", or "Jan-2022"
    let isForcedMonthOrMY = ( forcedSubType===subtypes.kMonthYear || forcedSubType===subtypes.kMonth );
    if( isForcedMonthOrMY || ! result.isDateTime )
        getMonthOrMonthYearFromString( string, result, forcedSubType );

    // test for YYYY-MM numeric strings, "2022/01" or "2022-01"
    let isForcedYearMonth = (forcedSubType===subtypes.kYearMonth);
    if( isForcedYearMonth || ! result.isDateTime )
        getYearMonthFromString( string, result, isForcedYearMonth );

    // test for year string, or more likely, force string like "1977-abcd" to a year on request
    let isForcedYear = (forcedSubType===subtypes.kYear);
    if( isForcedYear || ! result.isDateTime)
        getYearFromString( string, result, isForcedYear );

    // save formatting if object was supplied by caller
	if( result.isDateTime && oFormat  ) {
		oFormat.subtype = result.dateTimeSubtype;
		oFormat.value = result.dateMS;
        oFormat.formatString = result.formatString;
	}
	return( result.isDateTime ? result.dateMS : undefined );
}

    /**
     * Test a numeric data value for date-time formatting.  Possible only if forced,
     * or we recognize year values within certain common ranges.
     * @param number
     * @param oFormat -- object to hold subtype and numeric value valid for that subtype, if any
     * @param forcedSubType -- undefined or Dyn.dataUtil.subtypes value
     */
    static _numberToDateTime( number, oFormat, forcedSubType ) {
        let util = Dyn.dataUtil,
            subtypes = util.subtypes,
            newSubtype;
        if( Number.isFinite( number )) {
            // test for forced type change, if any
            switch( forcedSubType ) {
                case subtypes.kYear:
                    newSubtype = subtypes.kYear;
                    break;
                case subtypes.kMonth:
                    if( Number.isInteger(number) && Dyn.mathUtil.isInIntegerRange( number, 0, 12 )) {
                        newSubtype = subtypes.kMonth;
                    }
                    break;
                case subtypes.kDayOfWeek:
                    if( Number.isInteger(number) && Dyn.mathUtil.isInIntegerRange( number, 0, 7 )) {
                        newSubtype = subtypes.kMonth;
                    }
                    break;
            }
            // else test numeric value is in a known range
            // NOTE: we only use this very conservatively for Years in a limited range, could easily be wrong
            // there are too many reasons why numbers in 0-6 are not days, or 0-12 are not months, so we don't check.
            if( ! newSubtype ) {
                if( util.yearPattern.test( "" + number ) ) {
                    newSubtype = subtypes.kYear;
                }
            }
        }
        if( oFormat && newSubtype ) {
            oFormat.subtype = newSubtype;
            oFormat.value = number;
        }
    }

    /**
     * Test to see if the entire string is a valid English month, and if so, which one.
     * The test is case-insensitive; abbreviated but 3+ letters (no period at end) are OK.
     * Example: "mar", "march", "Marc", result is 2 (3rd month)
     * Example: "married", "mary", and "ma", result is -1 (non-match, or less than 3 characters)
     * @param string
     * @return {number} - month index 0-11, or -1 if not a month
     * @private
     */
    static _getMonthIndexFromString( string ) {
        let util = Dyn.dataUtil,
            index = util.kMonthsOfYearShortLC.indexOf( string.toLowerCase().substr(0, 3));
        if( index >= 0 ) {
            // now that we know which month from first 3 chars, test entire string length
            let length = Math.min( util.kMonthsOfYearLongLC[index].length, string.length );
            if( string.toLowerCase() === util.kMonthsOfYearLongLC[index].substr(0, length)) {
                return index; // 0-11
            }
        }
        return -1; // not a month
    }

    /**
     * Test to see if entire string is a valid English day-of-week, and if so which one.
     * Case insensitive, abbreviated but 3+ letters (no period at end), e.g. "sunday", "Mon".
     * @param string
     * @return {number} - day index 0-6, or -1 if not a day
     * @private
     */
    static _getDayOfWeekIndexFromString( string ) {
        let util = Dyn.dataUtil,
            index = util.kDaysOfWeekShortLC.indexOf( string.toLowerCase().substr(0, 3));
        if( index >= 0 ) {
            // now that we know which day-of-week from first 3 chars, test entire string length
            //		e.g. includes "sun" and "sunday" but excludes "sundance", "sunflower", and "su".
            let length = Math.min( util.kDaysOfWeekLongLC[index].length, string.length );
            if( string.toLowerCase() === util.kDaysOfWeekLongLC[index].substr( 0, length ) ) {
                return index; // 0-6
            }
        }
        return -1; // not a day of week
    }

static removeFalseDateTime( str, out ) {
	// remove common patterns that pass Date.parse() but are not dates
	const util = Dyn.dataUtil,
		startsWithLetters = /^[a-zA-Z]+/,
		hasParenthesis = /\(.*\)/,
		startsWithYYYYDash = /^[\d][\d][\d][\d]-[\d]+/,
		startsWithYYYYSpaceComma = /^[\d][\d][\d][\d](,|\s)/,
        numbersThenSymbols = /^\d+[^a-zA-Z\d\s:\u00C0-\u00FF]+$/,
        justDigits = /^[\d]+$/;

	if( startsWithLetters.test(str)) {
		var hasMonth = util.monthPattern.test( str ),
			hasDayOfWeek = util.dayOfWeekPattern.test( str );
		if( ! (hasMonth || hasDayOfWeek )) {
			out.isDateTime = false;  // dates that start with letters must be Jan, Sun, etc., otherwise not a date
		}
		if( ! Dyn.dataUtil.datePatternNonISO.test( str )) {
			out.isDateTime = false;  // dates that start with letters have at least 3 parts
			// e.g. 'March 25 2015', 'March 25, 2015'.  Exclude "March 2015 which is not valid in Safari or FireFox.
		}
	} else if( hasParenthesis.test(str)) {
		var noParenStr = str.replace( hasParenthesis, "" ), // remove parethesis
			hasDateWithoutParenthesis = util.datePattern.test( noParenStr );
		if( ! hasDateWithoutParenthesis ) {
			out.isDateTime = false; // if has parenthesis but not date, it is not a date time.
			// NOTE: this prevents "12 (1960-12-1)" from being interpreted as a date in Chrome, to be consistent with FireFox
		}
	} else if( util.yearMonthDigitsPattern.test( str ) ||
               util.yearMonthNamePattern.test(str)) {
        out.isDateTime = false; // "2022/12" or "2022-12" are Year-Month, not date-time (Chrome Date.parse() again)
    } else if( util.datePatternISO.test( str )) {
		if( ! startsWithYYYYDash.test( str )) {
			out.isDateTime = false;  // if has dash between letters but is not YYYY-MM-DD format, is is not a date-time
			// Note: only Chrome allows this, don't allow anywhere for consistency with Safari and FireFox.
		}
	} else if( startsWithYYYYSpaceComma.test( str )) {
		out.isDateTime = false; // "2015, Jan 25" and "2015 Jan 25" are not valid date formats
		// Note: only Chrome allows this, don't allow anywhere for consistency with Safari and FireFox.
	} else if( justDigits.test(str) || numbersThenSymbols.test( str )) {
        out.isDateTime = false; // "78", "78%", "78-" are not valid date formats
        // Note: only Chrome allows this, don't allow anywhere for consistency with Safari and FireFox.
    }
}

/**
 * Convert a date-time serial number to a local date string.
 * 		Does not show the time zone, we use a time zone neutral format.
 * 		Time zone conversions should happen at time of Date.parse() because of variations on time zone by input format.
 * @param dateTimeMS
 * @returns {string}
 */
static dateMsToLocalDateOnly( dateTimeMS ) {
	var local = new Date( dateTimeMS );
	//+ Dyn.dataUtil.kDateTimeZoneCorrection );
	//local.setMinutes(dateMS.getMinutes() - dateMS.getTimezoneOffset()); // alternate method to adjust time zone
	return local.toJSON().slice(0, 10); // get YYYY-MM-DD
}

static dateMsToLocalTimeOnly( dateTimeMS ) {
	var local = new Date( dateTimeMS );
	return local.toLocaleTimeString(); // get HH:MM:SS AM/PM
}

/**
 * Convert a duration serial number to a duration string for display to user.
 * Note: note that durations are positive and negative real numbers, e.g. -00:00:01.2345,
 * so that calculated durations like St.Deviation(duration) are also formatted correctly.
 * @param durationNum {number}-- +/- number of minutes or seconds (depending on subtype)
 * @param subtype {Dyn.dataUtil.subtype} -- HH:MM:SS or HH:MM format to use
 * @returns {string}
 */
static durationSerialToDurationString( durationNum, subtype, decimals ) {
    function twoDigits( value, overFlowOK = false ) {
        var decimalPlaces = Dyn.mathUtil.decimalPlaces( value ),
            oldLength = ("" + value).length,
            string = "00" + value, // add leading zeros
            newLength = 2;
        if( value !==0 && overFlowOK ) {
            // left-most part of duration can be more than 2 digits
            newLength = Math.max( 2, oldLength );
        }
        if( decimalPlaces > 0 ) {
            // right-most part of duration can have decimals 01:03.465
            newLength = Math.max(  2, Math.max( oldLength, decimalPlaces + 3 ));
        }
        return string.slice( string.length - newLength, string.length );
    }

    let negativeSign = (durationNum >= 0 ) ? "" : "-",
        absDuration = Math.abs( durationNum );
    // NOTE: must use modulo instead of % to fix Javascript round-off errors
    let seconds = Dyn.mathUtil.modulo( absDuration, 60 ),
        time = (absDuration - seconds ) / 60,
        minutes = Dyn.mathUtil.modulo( time, 60 ),
        hour = ( time - minutes ) / 60;
    if( subtype === this.subtypes.kDurationHM ) {
        minutes = Dyn.mathUtil.roundToDecimalPlaces( minutes + (seconds / 60 ), decimals); // add in seconds if any
        return negativeSign + twoDigits(hour, true) + ":" + twoDigits(minutes);
    } else {
        Dyn.assert( subtype === this.subtypes.kDurationHMS )
        seconds = Dyn.mathUtil.roundToDecimalPlaces( seconds, decimals );
        return negativeSign + twoDigits(hour, true) + ":" + twoDigits(minutes) + ":" + twoDigits(seconds);
    }
}

/**
 * Convert a duration string to a duration serial number for apply a value from user.
 * Note: note that durations are positive and negative real numbers, e.g. -00:00:01.2345
 * @param durationString {string}-- duration string example: 40:20:30 or 40:20.5
 * @param subtype {Dyn.dataUtil.subtype} -- HH:MM:SS or HH:MM format to use
 * @returns {number}
 */
static durationStringToDurationSerial( durationString, subtype ) {
	var isNegative = false;
	if(durationString.indexOf("-") === 0) {
		isNegative = true;
		durationString = durationString.substring(1);
	}
	var splits = durationString.split(":");
	var total = 0;
    if( subtype === this.subtypes.kDurationHM ) {
		total += parseInt(splits[0]) * 60 * 60; // hours
		total += parseInt(splits[1]) * 60; // minutes
    } else {
		total += parseInt(splits[0]) * 60 * 60; // hours
		total += parseInt(splits[1]) * 60; // minutes
		total += parseInt(splits[2]); // seconds
	}
	return isNegative? -total : total;
}

static dateMsToLocalDateTime( dateTimeMS ) {
	"use strict";
	var local = new Date( dateTimeMS );
	return local.toJSON().slice(0, 10) + " " + local.toLocaleTimeString(); // get HH:MM:SS AM/PM
}

static dateMsToLocalYear( dateTimeMS ) {
	"use strict";
	var local = new Date( dateTimeMS );
	return local.getFullYear();
}

static dateMsToLocalDayOfWeek( dateTimeMS, precision ) {
	var local = new Date( dateTimeMS ),
        day = local.getDay(), // integer [0-6]
        hour = local.getHours(), // integer [0-23]
        minute = local.getMinutes(), // integer [0-59]
        //seconds = local.getSeconds(),
        fraction = ((60 * hour) + minute ) / (60 * 60),
        dayString = Dyn.loc( Dyn.dataUtil.kDisplayDays[ local.getDay() ]); // Mon, Tues, etc.
    if( Dyn.mathUtil.notCloseToZero( fraction )) {
        dayString = Dyn.loc( "{{day}} + {{fraction}} days", { day:dayString, fraction:Dyn.mathUtil.roundToPrecision( fraction, precision ) } );
    }
    return dayString;
}

    /**
     * convert a real data value to a rotating display value in the array, e.g ["Sun","Mon",...],
     * handling +/- real numbers, discarding fraction.
     * @param value
     * @param array
     * @returns {*} -
     * @private
     */
    static _serialToArrayIndex( value, displayArray ) {
        const length = displayArray.length;
        let index = (value >= 0)
            ? (Math.floor( value ) % length)
            : ((length+(Math.floor(value)%length))%length);
        Dyn.assert( displayArray[index]);
        return displayArray[index];
    }

    /**
     * extract the fractional value of a rotating serial data value,
     * with special treatment for negative values.
     * examples: (1.2) => 0.2, (-1.2) => 0.8
     * @param value
     * @param length
     * @returns {number}
     * @private
     */
    static _serialToFraction( value ) {
        return value - Math.floor( value );
    }

    static monthSerialToString( monthSerialNumber, precision ) {
        let fraction = this._serialToFraction( monthSerialNumber ),
            string = Dyn.loc( this._serialToArrayIndex( monthSerialNumber, Dyn.dataUtil.kDisplayMonths )); // Jan, Feb, etc.
        if( Dyn.mathUtil.notCloseToZero( fraction )) {
            string = Dyn.loc( "{{month}} + {{fraction}} months", { month:string, fraction:Dyn.mathUtil.roundToPrecision( fraction, precision )} );
        }
        return string;
    }

    static dayOfWeekSerialToString( daySerialNumber, precision ) {
        let fraction = this._serialToFraction( daySerialNumber ),
            string = Dyn.loc( this._serialToArrayIndex( daySerialNumber, Dyn.dataUtil.kDisplayDays )); // Sun, Mon, etc.
        if( Dyn.mathUtil.notCloseToZero( fraction )) {
            string = Dyn.loc( "{{day}} + {{fraction}} days", { day:string, fraction:Dyn.mathUtil.roundToPrecision( fraction, precision )} );
        }
        return string;
    }

    /**
     * Convert a month-year internal numeric value to a date Serial.
     * @param monYear
     * @return {*}
     */
    static monthYearSerialToDate( monYear ) {
        var year = Dyn.mathUtil.floor( monYear / 12 ), // get whole year, e.g. 2022
            month = Dyn.mathUtil.floor( monYear - (year * 12)) + 1; // get whole month 1, 2...
        return new Date(""+year+"-"+month+"-01").valueOf();
    }

    /**
     * Convert a dateSerialNo to a Year-Mon.
     * @param dateSerialNo
     * @return {*}
     */
    static dateSerialToMonthYear( dateSerialNo ) {
        var date = new Date(dateSerialNo);
        console.log(date.getFullYear()*12 + date.getMonth());
        return date.getFullYear()*12 + date.getMonth();
    }

    /**
     * Convert a month-year or year-month internal numeric value (serial number) to a month-year/year-month string.
     * month-year serial numbers are created by stringToDateTime() for type kMonthYear, or kYearMonth,
     * or calculations of those values (e.g. for tick marks, reference line on axis, etc.
     * @param monYearSerialNo - serial number or calculated fraction value of serial numbers.
     * @param precision - precision to use for fractional values
     * @param format {string} - desired format ["YYYY-MM"|"mmm-YYYY"]
     * @return {*}
     */
    static monthYearSerialToString( monYearSerialNo, precision, format ) {
        var year = Dyn.mathUtil.floor( monYearSerialNo / 12 ), // get whole year, e.g. 2022
            month = Dyn.mathUtil.floor( monYearSerialNo - (year * 12)), // get whole month 0, 1, 2...
            fraction = monYearSerialNo - (year*12 + month), // get fractional value of month if any
            monthIndex = month % Dyn.dataUtil.kDisplayMonths.length, // convert to month index 0-11
            str, wholeStr, fractionalStr;
        // get year string YYYY
        year = ""+year; // '2022', '-304' (bce), etc.
        // test for year last, else year is first
        if( /YYYY$/.test(format) ) {
            wholeStr = "{{month}}-{{year}}";
            fractionalStr = "{{month}}-{{year}} + {{fraction}} months";
        } else {
            wholeStr = "{{year}}-{{month}}";
            fractionalStr = "{{year}}-{{month}} + {{fraction}} months";
        }
        // test for Month 3-character alpha, else month is 2 digits
        if( /Mmm/.test(format)) {
            month = Dyn.loc( Dyn.dataUtil.kDisplayMonths[ monthIndex ] ); // Jan, Feb, etc.
        } else {
            month = ("0"+(month+1)).slice( -2 ); // '01', '02', ...'12'
        }
        // assemble and translate
        if( Dyn.mathUtil.notCloseToZero( fraction ) ) {
            str = Dyn.loc( fractionalStr, {
                    year:year, month:month, fraction:Dyn.mathUtil.roundToPrecision( fraction, precision ) } );
        } else {
            str = Dyn.loc( wholeStr, { year:year, month:month } );
        }
        Dyn.assert( this.yearMonthOutputFormats.includes(format), "unknown format: "+format );
        return str;
    }

static yearSerialToLocalYear( yearSerialNumber, precision ) {
    // should we show fractional year values in some special way? for now, no.
    var integer = Dyn.mathUtil.floor( yearSerialNumber ), // convert to whole year, e.g. 2016
        fraction = yearSerialNumber - integer, // get fractional value if any
        yearString = "" + integer;
    if( Dyn.mathUtil.notCloseToZero( fraction )) {
        yearString = Dyn.loc( "{{year}} + {{fraction}} years", { year:yearString, fraction:Dyn.mathUtil.roundToPrecision( fraction, precision )} );
    }
    return yearString;
}

/**
 *  getDataType()
 *  Return the type of data for the case or table cell.
 *  Returns a string, one of "number", "string", or "missing", for consistent handling of data values.
 *  Also sets format parameters in option 'caseFormat' object.
 *  Used in coordination with normalizeCaseValueString() to handle any incoming or calculated data.
 *  E.g. boolean values case values should be treated as type "string", converted to "true", "false".
 *  E.g. NaN, Null, undefined, "" and other non-numeric, non-string, empty values converted to "missing" (kMissingValue)
 *
 * @param caseValue {*} -- number or string or other data value
 * @param [caseFormat] {{}}||null} -- optional object to store type-specific formatting details or subtype.
 * @param [forcedDataType] {string}  -- option Dyn.dataUtil.types value to force data type conversions where possible
 * @param [forcedSubType] {string} -- optional Dyn.dataUtil.subtypes value to force data type conversions where possible
 * @returns {string} -- type string {"number"|"string"|"missing"}
 */
static getDataType( caseValue, caseFormat, forcedDataType, forcedSubType ) {
	if( caseFormat ) {	// clear any previous values in optional caseFormat specification
		caseFormat.subtype = undefined;
		caseFormat.value = undefined;
        caseFormat.formatString = undefined;
	}

	switch( typeof caseValue ) {
        case "number":
            this._numberToDateTime( caseValue, caseFormat, forcedSubType ); // test for number:year format
            return (isFinite( caseValue ) ? "number" : "missing");
        case "string": {
            // type of non-empty string is "string", except...
            let type;
            if( caseValue === "" || caseValue === this.kStringMissingValue ) {
                type = "missing"; // type "missing" if empty string, or special kStringMissingValue ("-")
            }
            if( !type ) {
                let valueFr = Dyn.fractionData.fractionStringToNumber( caseValue, caseFormat, (forcedSubType===this.subtypes.kFraction));
                if( isFinite( valueFr )) {
                    type = "number"; // fraction string, e.g. "1/2" or "-1 20/300". Note that "1/2024" may also be a Month-Year, Date, etc.
                }
            }
            if( !type ) {
                let valueDT = this.stringToDateTime( caseValue, caseFormat, forcedSubType ); // {number} or NaN
                if( isFinite( valueDT ) ) {
                    type = "number"; // date numeric value is in caseFormat.value, date format in caseFormat.subType
                }
            }
            if( !type ) {
                // convert to a number even if not a numeric type? e.g. string with commas, mix of letters/numbers?
                let valueEN = this.stringExtractNumber( caseValue, caseFormat, (forcedDataType === "numerical"));
                if( isFinite( valueEN ) )
                    type = "number";
                else {
                    type = "string"; // default to string
                }
            }
            //if( caseFormat?.subtype ) console.log("getDataType()", caseValue, "=>", caseFormat, "forced ="+forcedDataType+":"+forcedSubType );
            return type;
        }
        case "boolean":
            // treat true or false as strings, see normalizeCaseValueString()
            return "string";
        case "undefined":
        default:
            // treat "undefined","object", "function", etc as missing values
            // coordinate with normalizeCaseValueString() which converts "missing" data to kMissingValue
            return "missing";
    }
}

/**
 * Convert the value given two one of three standard primitive types:
 * 	a number, string, or missing value,
 * 	for ease of calculations on the value.
 *
 * 	This eliminates javascript types which are not data types,
 * 	including: boolean, object, array, function, null, etc.
 * 	And also converts strings like "10" to the number 10, etc.
 * @param caseValue -- data value, which may be any javascript type (but usually is a string)
 * @returns {null|string|number}
 */

static normalizeCaseValueString( caseValue ) {
	let valAsNumber	= Number( caseValue ),
		dataTypeJS = typeof caseValue,
		finalVal,
		finalType;

	// convert to a normal value
	if ( Number.isFinite( valAsNumber ) && (dataTypeJS === "number" || (dataTypeJS ==="string" && caseValue.length))) {
		// if it is a number or a string that looks like a number, treat it as a number (e.g. 123, "123", etc.)
		// if it does not look like a number, DON'T convert to a number (e.g. null, undefined, true, false, "", etc.)
		// Also, ordinary numbers may also be valid dates; keep them as numbers, so they can be converted to either type.
		finalVal = valAsNumber; // ordinary number
		finalType = "number";
	} else if ( dataTypeJS==="string" && caseValue !== "" && caseValue !== Dyn.dataUtil.kStringMissingValue ) {
		finalVal = String( caseValue ); // make sure it really is a string (not boolean, etc)
		finalType = "string";
	} else if( dataTypeJS==="boolean" ) {
        finalVal = caseValue.toString(); // convert true/false to "true"/"false", important for formula evaluator
        finalType = "string";
    } else {
        // null, undefined, empty string, NaN, +/- Infinity, object, function, etc.
        finalVal = Dyn.dataUtil.kMissingValue;
        finalType = "missing";
	}
	// test converted value for consistency with getDataType()
	//if( ! Dyn.assert( Dyn.dataUtil.getDataType( finalVal )===finalType, "normalizeCaseValueString() type mismatch"))
    //    console.log( caseValue, "type", finalType, "!==", Dyn.dataUtil.getDataType( finalVal ));
	return finalVal;
}

/**
 * calcStats()
 * Return a collection of computed statistics on the case array.
 * @param attribCaseArray {[]} - array of case values processed by normalizeCaseValueString()
 * @param options {*} - those attribute options that influence the statistics
 * @param {string} [options.attributeType] - "categorical", "numeric", or undefined
 * @param {array} [options.fixedCategories] - optional fixed categorical sort order and additional categories
 * @param {string} [options.isSortAscending] - category sort order, if categorical and no fixed category order
 * @param {function|null} [options.attrFilter] --filter used to filter out categories
 * @param {function|null} [options.filterFn] --function to filter out cases
 * @param {number} [options.filterID] - unique ID of this filter configuration, or undefined
 * @returns {Dyn.dataUtil.AttributeStats}
 */
static calcStats( attribCaseArray, options ) {
	// Dyn.time( 'dataUtil:calcStats', 'Start' );
    // console.log("calcStats "+options.name );
	// define stats parameters
	var stats = new Dyn.dataUtil.AttributeStats(), // create a new stats object
		isForcedNumerical = options.dataType === "numerical",
		isForcedCategorical = options.dataType === "categorical",
		i = 0,
		ii = attribCaseArray.length,
		caseFormat = {},
		rawValue, caseValue, caseType, finalType;

	stats.filterID = options.filterID; // unique ID or undefined
	stats.min = Dyn.mathUtil.MAX_SAFE_INTEGER;
	stats.max = Dyn.mathUtil.MIN_SAFE_INTEGER;
	stats.categories = addFixedCategories( options.fixedCategories, options.attrFilter );
	stats.attribIndex = options.attribIndex;

	// compute stats from case values
	for( ; i < ii; i++ ) {
		if( options.filterFn && options.filterFn( i ) ){
			continue; // skip this case, it is filtered out
		}
		rawValue = caseValue = attribCaseArray[ i ];
		// If togleDataType not provided, continue using the actual datatype
		// missing casetype still takes priority
		caseType = Dyn.dataUtil.getDataType( caseValue, caseFormat, options.dataType );
		finalType = ( isForcedCategorical && caseType === "number" ) ? "string" :
					(( isForcedNumerical && caseType === "string" ) ? "missing" :
					caseType ); // "missing", "number", or "string"
		switch( finalType ) {
		case "missing":	// missing value or string forced to be number
			stats.missingCaseCount++;
			break;
		case "number": // number or string with numeric subtype
			if( caseFormat.subtype ) {
                caseValue = caseFormat.value; // use the subtype-specific case value (e.g. date in milliseconds from 1970/01/01)
				// count number of cases with each subtype, like Dyn.dataUtil.subtypes.kDateTime, and use the special case value
                stats.subtypeCounts[ caseFormat.subtype ] = ( stats.subtypeCounts[ caseFormat.subtype ] || 0 ) + 1;
                if( caseFormat.formatString ) {
                    stats.formatCounts[ caseFormat.formatString ] = ( stats.formatCounts[ caseFormat.formatString ] || 0 ) + 1;
                }
			}
			stats.numericCaseCount++;
			addToNumbers( caseValue, caseFormat );
			addToCategories( rawValue.toString() ); // numbers create valid categories, if treated as categorical
			break;
		case "string": // natural string or number forced to be string
			stats.categoricCaseCount++;
			addToCategories( caseValue.toString() );
			break;
		default:
			console.log( "calcStats unexpected value: "+Dyn.dataUtil.getDataType( caseValue ));
		}
	}

	// clean up numeric analysis
	if( stats.min === Dyn.mathUtil.MAX_SAFE_INTEGER && stats.max === Dyn.mathUtil.MIN_SAFE_INTEGER ) {
		stats.min = stats.max = undefined; // no numbers so mark our scale as undefined (in case we want to know)
	}
	// clean up categorical analysis
	if( stats.isCategorical()) {
		if( options.fixedCategories ) {
            if( options.sparseCategories ) {
                // remove unused numeric-interval categories #1326
                stats.categories = stats.categories.filter( cat => (stats.categoricIndvCaseCount[cat] > 0));
            }
            if( ! options.isSortAscending ) {
                stats.categories.reverse(); // flip fixed/dragged categories
            }
        } else {
			// arrange the categories in order (for sorting case values, color values, etc)
			stats.categories.sort( options.isSortAscending ?
				Dyn.dataUtil.compareStringsWithNumbers :
				Dyn.dataUtil.compareStringsWithNumbersDescending );
		}
	} else {
		stats.categories = []; // remove to save sort time and memory, e.g. don't need sorted list of all numeric values
	}

	// Dyn.time( 'dataUtil:calcStats', 'End' );
	return stats;

	function addToCategories( caseValue ) {
		if( stats.categories.indexOf( caseValue ) === -1 ) {
			stats.categories.push( caseValue ); // add category string
		}
        if(stats.categoricIndvCaseCount[caseValue]) {
            stats.categoricIndvCaseCount[caseValue]++;
        } else {
            stats.categoricIndvCaseCount[caseValue] = 1; // start count of cases for this category string
        }
	}
	function addToNumbers( caseValue, caseFormat ) {
		if( stats.min > caseValue ) {
			stats.min = caseValue;
		}
		if( stats.max < caseValue ) {
			stats.max = caseValue;
		}
        if( caseFormat.fractionDecimals ) {
            // if we found a fraction, get the numericDecimals from the denominator
            if( stats.numericDecimals < caseFormat.fractionDecimals ) {
                stats.numericDecimals = caseFormat.fractionDecimals;
            }
        } else {
            // else get numericDecimals from the value
            let dp = Dyn.mathUtil.decimalPlaces( caseValue );
            if( dp && stats.numericDecimals < dp ) {
                stats.numericDecimals = dp;
            }
        }
        if( caseFormat.trailingZerosCount ) {
            stats.hasTrailingZeros = true;
            // if we are including trailing zeros, expand the numeric decimals count
            stats.numericDecimals = Math.max( stats.numericDecimals, caseFormat.trailingZerosCount);
        }
	}
	function addFixedCategories( fixedCategories, attrFilter ) {
		var categories = [];
		if( fixedCategories ) {
			// optionally start with fixed categories to determine sort order and add categories not in cases
			if( attrFilter && attrFilter.categories ) {
				// copy fixed categories only if not in attribute filter
                categories = fixedCategories.filter( (cat) => ! attrFilter.categories.includes(cat));
			} else {
				// copy all attribute's fixed categories if any
				categories = Dyn.dataUtil.clone( fixedCategories );
			}
		}
		return categories;
	}
}


// field names for Dyn.dataUtil.calcNiceIntervals() boolean options
//  e.g. options[EIntervalRange.irLowerOutside] = true;
static EIntervalRange = {
    irLowerBeyond: "irLowerBeyond",     // nice min value should be < initial min
    irUpperBeyond: "irUpperBeyond",     // nice max value should be > initial max

    irLowerOutside: "irLowerOutside",   // nice min value should be <= initial min
    irUpperOutside: "irUpperOutside",   // nice max value should be >= initial max

    irLowerExact: "irLowerExact",       // nice min value should be == initial min
    irUpperExact: "irUpperExact",       // nice max value should be == initial max

    irLowerInside: "irLowerInside",     // nice min value should be >= initial min
    irUpperInside: "irUpperInside",     // nice max value should be <= initial max

    irExactIntervals: "irExactIntervals" // number of intervals is fixed, width is not
}

/**
 * find nice numeric intervals and endpoints from the given data min and max.
 * Used to find histogram intervals, numeric intervals for attributes, and axis endpoints and tickmarks.
 *  Options:
 *      See Dyn.EIntervalRange for min/max options like options.irLowerOutside, options.irUpperOutside
 *      Options { isAtMin, isAtMax, exactWidth } are for +/- width settings, to get a nice min and max
 *      options { subtype, formatString } are for setting intervals for custom units like fractions, dates, etc.
 * @param dataMin
 * @param dataMax
 * @param targetNumberOfTicks
 * @param options {*} -- see above for the many variations on what are "nice" intervals.
 * @returns {NiceIntervals}
 */
static calcNiceIntervals( dataMin, dataMax, targetNumberOfTicks, options ) {
	Dyn.assertFiniteNumeric( dataMin );
	Dyn.assertFiniteNumeric( dataMax );
	Dyn.assertFiniteNumeric( targetNumberOfTicks );
	let startingNumIntervals = targetNumberOfTicks || 5,  // target number of intervals
		startingRangeWidth = Math.abs( dataMax - dataMin ),  // width of original interval range
		startingIntervalWidth = startingRangeWidth / startingNumIntervals,  // first approx. at increment
		newRange = new NiceIntervals( dataMin, dataMax, startingNumIntervals, startingIntervalWidth );

	// look at options that modify intervals
	options = options || {}; // empty object if no flags are set, e.g. { Dyn.EIntervalRange.irLowerOutside: true }
    // console.log("calcNiceIntervals start dataMin-Max:", dataMin+" - "+dataMax, "targetNumberOfTicks: "+targetNumberOfTicks, options );
	if( options.subType ) { // adjust widths to base unit
        this._getNiceRangeDivisorForSubType( options, dataMin, dataMax );
	}
    if( isFinite( options.maxDecimals ) && ! isFinite( options.minIntervalWidth )) {
	    // if minIntervalWidth not yet specified ( by getNiceRangeDivisorForSubType() )
        // define minIntervalWidth by the maximum number of decimal places after zero (0+)
        options.minIntervalWidth = Dyn.mathUtil.decimalPlacesToIntervalWidth(options.maxDecimals);
        Dyn.assert( options.minIntervalWidth <= 1.0 );
    }

    // calc nice intervals
	newRange.minorIntervalWidths = this._calcNiceIntervalWidth( startingRangeWidth, startingIntervalWidth, startingNumIntervals, options );
    newRange.intervalWidth = newRange.minorIntervalWidths[0];

	// calc nice endpoints
	if( options.customOrigin ) { // shift to new origin for custom endpoint adjustments (e.g. Days of week start on Sunday 00:00:00 AM)
		newRange.min -= options.customOrigin;
		newRange.max -= options.customOrigin;
	}
    this._calcEndPointsFromNiceIntervalWidth( newRange, options ); // calc min, max, numIntervals
	if( options.customOrigin ) { // convert custom origin adjustments back to normal
		newRange.min += options.customOrigin;
		newRange.max += options.customOrigin;
	}

	// calc interval values
	newRange.values = [];
    for( let v=newRange.min, i=0; newRange.numIntervals >= i++; ) {
        newRange.values.push( v );
        v = ( options.subType !== Dyn.dataUtil.subtypes.kFraction )
            ? Dyn.mathUtil.roundToPrecision( v + newRange.intervalWidth )
            : v + newRange.intervalWidth;
    }
    // preserve isAtMin, isAtMax even if min adjusted slightly
    newRange.isAtMin = options.isAtMin;
    newRange.isAtMax = options.isAtMax;
    //if (options.subType) console.log("calcNiceIntervals end", newRange );
	return newRange;
}

    // static niceIntervalWidthOptions = { // nice tick increment options (e.g. multiples of 2 or 5, etc.)
    //     major: [10, 5, 2, 1, 0.5],      // major intervals options, after bringing target width to range [1.0,10.0)
    //     minor1: [ 5, 2, 1, 0.5, 0.25],  // corresponding minor interval options 1
    //     minor2: [ 2, 1, 0.5, 0.2, 0.1], // corresponding minor interval options 1
    // }
    // nice tick increment options:
    //  after bringing target width to the range [1.0,10.0), e.g. range 1-100 becomes divisor 0.
    //  then make intervals every 1/n units, which ever is closest to the requested, non-nice width.
    //  e.g. range 40-100 = 60 wide, adjusted to range of 6.0, do we make intervals every 1 unit, 1/2 units, 1/5 units?
    //  the request is 5 intervals, so 6.0/5 = 1.2 units.
    //  we check all to see which nice value is closest to the requested
    static niceIntervalWidthOptions = [1, 2, 5, 10, 20, 40, 100];

    static fractionWidthOptions = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20];

/**
 * Find nice numeric intervals and endpoints from the given data min and max.
 * @param iOrigRangeWidth - range of data, e.g, 12 units wide
 * @param iStartingIntervalWidth - starting width, e.g. 12/3 = 4 units per interval
 * @param iNumIntervals
 * @param options {*} - optional minimum interval width, etc
 * @returns {number[]} - nice interval width to use, nice[0] is the major width, nice[1-2] are possible minor intervals
 */
static _calcNiceIntervalWidth( iOrigRangeWidth, iStartingIntervalWidth, iNumIntervals, options ) {
    if( options.exactWidth ) {
        let exactWidths = [ options.exactWidth ];
        if( options.minIntervalWidth > options.exactWidth ) {
            exactWidths[0] = options.minIntervalWidth;
        }
        if( options.minorWidth ) {
            exactWidths.push( options.minorWidth );
        }
        return exactWidths; // [0] is major interval, [1] is possible minor interval
    } else {
        let divisor = Math.pow( 10, Dyn.mathUtil.floor( Math.log( iStartingIntervalWidth ) / Math.log( 10.0 ) ) ), // divisor to bring increment into range [1.0,10.0)
            niceIndex = 0,
            niceWidths = this._getNiceWidthOptionsForSubtype( options, divisor );

        // find nice increment closest to first approximation
        let minDist = Number.MAX_VALUE;
        niceWidths.forEach( ( w, index ) => {
            let dist = Math.abs( iNumIntervals / (iOrigRangeWidth / (10 * divisor / w)) - 1 );	// distance
            if( dist < minDist ) {  // save the distance and index of the smallest nice increment
                minDist = dist;
                niceIndex = index;
            }
            //console.log( 'try:'+w+" scaled:"+ 10*divisor/w, dist, '-->', minDist );
        } );

        // scale to original power of 10 for new interval width
        let nice = niceWidths.slice( niceIndex ) // remove increments > nice increment
            .filter( ( n, i ) => (niceWidths[niceIndex + i] % niceWidths[niceIndex] === 0) ) // remove increments not even multiples of nice increment
            .map( ( n ) => 10 * divisor / n ) // scale to original power of 10 for new interval width
        if( options.minIntervalWidth ) { // ignore if undefined or zero
            if( nice.length < 1 || nice[0] < options.minIntervalWidth ) {
                nice[0] = options.minIntervalWidth; // restrict major interval width
                nice.length = 1; // remove minor intervals, too small
            } else {
                nice = nice.filter( ( n, i ) => (n >= options.minIntervalWidth && i < 2) );
            }
        }

        //console.log("calcNiceIntervalWidth", options.subType, options.formatString, "nice:", nice, "divisor:"+divisor, "niceWidth:"+niceWidths[niceIndex][0], options.customUnit, "started with:", iStartingIntervalWidth, iNumIntervals );
        return nice; // [0] is major interval, [1+] are possible minor intervals
    }
};

    static _getNiceWidthOptionsForSubtype( options, divisor ) {
        let niceWidths = this.niceIntervalWidthOptions;
        switch( options.subType ) {
            case Dyn.dataUtil.subtypes.kFraction:
                if( options.customUnit && divisor < 1 && Number.isFinite( options.minIntervalWidth ) ) {
                    // find nice fractional interval widths; warning, this list may be empty after filtering;
                    // caller should use options.minIntervalWidth in that case.
                    let fwo = Array.from( this.fractionWidthOptions ),
                        scaledCustomUnit = options.customUnit * 10*divisor; // e.g. fractional denominator scaled to width divisor
                    niceWidths = fwo.filter( ( w ) => {
                            // filter out nice major divisors don't divide nicely with customUnit
                            let ok = (w > scaledCustomUnit || w < 2) ? false : (scaledCustomUnit % w === 0);
                            //console.log( "getNiceWidthOptionsForSubtype", w, scaledCustomUnit, ok );
                            return ok;
                        } );
                    if( scaledCustomUnit > fwo.at(-1) && options.customUnit < fractionData.maxNiceDivisor ) {
                        fwo.push( scaledCustomUnit ); // customUnit/ could be a nice width, 24 for tick marks every 1/24 unit.
                    }
                }
                break;
        }
        //if (options.subType) console.log("getNiceWidthOptionsForSubtype", divisor, options.customUnit, niceWidths );
        return niceWidths;
    }

    /**
     * Get a non-standard divisor for special types (Dates and Times), if any.
     * Standard numeric ranges get divisors that are powers of 10, or 2, or 5, etc.
     * Dates and times get divisors based on the number of milliseconds per day, per hour, per year, etc.
     * @param options {*} - hints from/to calcNiceIntervals()
     * @param {Dyn.mathUtil.Range} iRange
     * @param iAttributeSubType
     * @returns {number|undefined} Positive number or undefined.
     */
    static _getNiceRangeDivisorForSubType( options, dataMin, dataMax ) {
        const iRange = new Dyn.mathUtil.Range( dataMin, dataMax ),
            iAttributeSubType = options.subType,
            formatString = options.formatString,
            width = Math.abs( iRange.width() ),
            minUnits = 5, // want at least 5 units of what ever the divisor is (e.g. 5 years to show years as divisor)
            subtypes = Dyn.dataUtil.subtypes;
        let divisor,        // default to undefined
            minIntervalWidth;  // default to undefined

        switch( iAttributeSubType ) {
            case subtypes.kDateTime:
            case subtypes.kDate:
            case subtypes.kTime:
                if( width >= minUnits * Dyn.dataUtil.kYearInMS ) {
                    divisor = Dyn.dataUtil.kYearInMS;
                } else if( width >= minUnits * Dyn.dataUtil.kDayInMS ) {
                    divisor = Dyn.dataUtil.kDayInMS;
                } else if( width >= minUnits * Dyn.dataUtil.kHourInMS ) {
                    divisor = Dyn.dataUtil.kHourInMS;
                } else if( width >= minUnits * Dyn.dataUtil.kMinuteInMS ) {
                    divisor = Dyn.dataUtil.kMinuteInMS;
                } else if( width >= minUnits * Dyn.dataUtil.kSecondInMS ) {
                    divisor = Dyn.dataUtil.kSecondInMS;
                }
                // else no divisor needed if smaller than multi-second range width
                break;
            case subtypes.kDayOfWeek:
                divisor = 1; // one day
                minIntervalWidth = 1; // to prevent tick marks with fractional values from appearing
                break;
            case subtypes.kMonth:
            case subtypes.kMonthYear:
            case subtypes.kYearMonth:
                if( width >= minUnits * 12 ) {
                    divisor = 12; // one year in months
                } else {
                    divisor = 1; // one month
                }
                minIntervalWidth = 1; // to prevent tick marks with fractional values from appearing
                break;
            case subtypes.kYear:
                minIntervalWidth = 1; // to prevent tick marks with fractional values from appearing
                break;
            case subtypes.kDurationHMS:
            case subtypes.kDurationHM:
                if( width >= minUnits * 3600 ) {
                    divisor = 3600; // one hour in seconds
                } else if( width >= minUnits * 60 ) {
                    divisor = 60;   // one minute in seconds
                } else if( width >= minUnits * 1 ) {
                    divisor = 1;    // one second
                }
                minIntervalWidth = 1; // to prevent tick marks with fractional values from appearing
                break;
            case subtypes.kFraction:
                if( width < minUnits && formatString ) {
                    let denominator = fractionData.formatStringToDenominator( formatString );
                    if( denominator > 1 && denominator < fractionData.maxNiceDivisor  ) {
                        // data range is < 5 units (e.g. 3-6=4 units), so fractional tick marks may be necessary
                        // use the denominator or a whole fraction of the denominator as the tick mark.
                        divisor = denominator; // e.g. divide every 3 units if fractions are like 1/3, 2/3, etc.
                        minIntervalWidth = 1/denominator; // no tick labels less than denominator
                    }
                }
                break;
            //case subtypes.kExtractedNumber:
            default:
            // divisor is undefined
        }
        options.customUnit = divisor;
        options.customOrigin = 0;
        options.minIntervalWidth = minIntervalWidth;
        //if( Number.isFinite(divisor)) console.log( "getNiceRangeDivisorForSubType() divisor=" + divisor, "range:"+iRange, iAttributeSubType );
    }

    /**
 * Find nice endpoints nice axis endpoints once we have computed the interval width.
 * @param ioRange
 * @param {number} ioRange.min -- initial and updated range minimum value
 * @param {number} ioRange.max -- initial and updated range maximum value
 * @param {number} ioRange.numIntervals -- initial and updated number of intervals from min to max
 * @param options
 */
static _calcEndPointsFromNiceIntervalWidth( ioRange, options ) {
	"use strict";

	// adjust range minimum
	if ( 		options.irLowerBeyond ) {
		ioRange.min = ioRange.intervalWidth * Dyn.mathUtil.ceiling( (ioRange.min / ioRange.intervalWidth) - 1 );
	} else if ( options.irLowerOutside ) {
		ioRange.min = ioRange.intervalWidth * Dyn.mathUtil.floor( ioRange.min / ioRange.intervalWidth );
	} else if ( options.irLowerExact ) {
		// leave as is
	} else /* ( options.irLowerInside ) */ {
		ioRange.min = ioRange.intervalWidth * Dyn.mathUtil.ceiling( ioRange.min / ioRange.intervalWidth );
	}

	// adjust range maximum
	if ( 		options.irExactIntervals ) {
		ioRange.max = ioRange.min + ( ioRange.intervalWidth * ioRange.numIntervals );
	} else if ( options.irUpperBeyond ) {
		ioRange.max = ioRange.intervalWidth * (1 + Dyn.mathUtil.floor( ioRange.max / ioRange.intervalWidth ));
	} else if (	options.irUpperOutside ) {
		ioRange.max = ioRange.intervalWidth * Dyn.mathUtil.ceiling( ioRange.max / ioRange.intervalWidth );
	} else if (	options.irUpperExact ) {
		// leave as is
	} else /* ( options.irUpperInside ) */ {
		ioRange.max = ioRange.intervalWidth * Dyn.mathUtil.floor( ioRange.max / ioRange.intervalWidth );
	}
	// now get number of intervals by dividing new range by new interval width
	if ( ! 	options.irExactIntervals ) {
		var tRangeWid = Math.abs( ioRange.max - ioRange.min );
		ioRange.numIntervals = Dyn.mathUtil.ceiling( tRangeWid / ioRange.intervalWidth );
	}

	// javascript requires extra rounding to whole numbers to format correctly
    // don't use for fractions so that values like 0.3333333333333 format to "1/3" correctly
    if( options.subType !== Dyn.dataUtil.subtypes.kFraction ) {
        ioRange.min = Dyn.mathUtil.roundToPrecision( ioRange.min );
        ioRange.max = Dyn.mathUtil.roundToPrecision( ioRange.max );
    }
}

/**
 * Return the interval index that the numeric case value belongs in, -1 for none.
 * Standard rule of intervals applies:
 *   most bins: (min <= value < max)
 * 	 last bin: (min <= value <= max)
 * @param ascendingNumericArray {[number]} -- sorted array of minimum values of each interval, max==min of next interval.
 * @param numericCaseValue {number}
 * @returns {number}
 */
static calcIntervalIndex( ascendingNumericArray, numericCaseValue ) {
	"use strict";
	var i=0,
		j=ascendingNumericArray.length,
		min;
	for( i=0; i < j; i++ ) {
		min = ascendingNumericArray[i];
		if( i < j-1 ) {
			min -= (min!==0?min:1)*Dyn.mathUtil.kTolerance; // most intervals (min <= value < max)
		} else {
			min += (min!==0?min:1)*Dyn.mathUtil.kTolerance; // last interval (min <= value <= max)
		}
		if( numericCaseValue < min ) {
			return i - 1; // in previous interval
		}
	}
	return -1; // higher than last interval.
}

    /**
     * Should we display minor tick marks or grid lines? Distance between tick marks? How many?
     * Input is the intervals.minorIntervalWidths array, which has several options
     * computed by Dyn.dataUtil.calcNiceIntervalWidth();
     * @returns {{hasMinor: boolean, dataWidth: number, howMany: number }}
     */
    static calcMinorTickData( coordMap, minorIntervalWidths, majorIntervalWidth ) {
        // Note: axis intervals are all in data coordinates
        const minPixelsPerMinorTick = 10;
        const minDistBetweenTicks = coordMap.inverseScaleAbs( minPixelsPerMinorTick ); // view to data coords
        let data = { hasMinor: false };
        if( minorIntervalWidths ) {
            minorIntervalWidths.forEach( (width,index) => {
                // find the smallest minor interval >= minMinorTickDistancePixels
                if( width >= minDistBetweenTicks ) {
                    data.dataWidth = width; // distance between minor tick marks in data units (e.g. 4 = 4 inches)
                    data.hasMinor = (index > 0); // ignore 0th index which is major interval
                }
            });
        }
        if( data.hasMinor ) {
            // e.g. major interval every 20 units, minor interval every 5, then howMany = (20/5) = 4.
            // but ignore overlap, so major ticks at 0 and 20, minor ticks at 5, 10, 15, actual # is (20/5)-1 = 3.
            data.howMany = Math.round( majorIntervalWidth / data.dataWidth );
        }
        return data;
    }

/**
 * Get the correct gap value for interval displays that don't overlap.
 * e.g. width 5, gap 1 => 0-4, 5-9, 10-14, etc. instead of 0-5, 5-10, 10-15
 * e.g. width 1, gap 0 => 1, 2, 3 instead of 1-1, 2-2, 3-3
 * e.g. width 0.1, gap 0.01 => 0-0.09, 0.1-0.19 instead of 0-0.1, 0.1-0.2
 * @param attributeDecimals -- minimum decimal places of attribute's cases
 * @param intervalWidth -- width of intervals
 * @returns {number}
 */
static getGapForIntervalWidth( attributeDecimals, intervalWidth ) {
    let decimals = Dyn.mathUtil.decimalPlaces( intervalWidth );
    if( attributeDecimals >= decimals ) {
        decimals = attributeDecimals; // prefer attribute's .numericDecimals setting, e.g. 0 for integers
    } else {
        decimals++; // e.g. width of 0.1, use gap of 0.01, for 0-0.09, 0.1-0.19, 0.2-0.29,
    }
    let gap = Math.pow(10, - decimals );
    return gap;
}

/**
 * Sort compare function for strings with numerical values
 * the other way to do is use open source alphnum sorter
 * instead of doing regex, we can check on the underlying chart value
 * This gives a correct numeric sort if the strings start with valid floating-point numbers.
 * @params a {String}
 * @params b {String}
 * @returns {number}
 */
static compareStringsWithNumbers(a, b) {
	// divide the value into (possibly) numeric, and definitely non-numeric substrings
	const reSubs = /[-.\d]+|[^-.\d]+/g; // contains [-1234567890.] or not
	// check if string as numeric value
	const reDigit = /[-]{0,1}[\d]+[.]{0,1}[\d]*/; // match 123 or -123 or -1.23 or 1.23 etc

	// compare the values in same case
	a = a.toString().toUpperCase();
	b = b.toString().toUpperCase();

	// separates the strings into substrings that have only numbers, or not, e.g. ["-1.23", " miles"]
	let aSubs = a.match(reSubs),
        bSubs = b.match(reSubs),
	    isDigitPart;

	// If the first substring of aSubs and bSubs are both numbers, or both non-numbers
	if(aSubs && bSubs &&
		(isDigitPart = reDigit.test(aSubs[0])) === reDigit.test(bSubs[0])) {
		// rotate through each substring part to compare the overall strings;
		let len = Math.min(aSubs.length, bSubs.length);
		for(let i = 0; i < len; i++) {
			let aSub = aSubs[i],
			    bSub = bSubs[i];
			// if comparing digits, convert them to numbers
			if(isDigitPart) {
				aSub = parseFloat( aSub );
				bSub = parseFloat( bSub );
			}
			// check if substrings aren't equal, return either -1 or 1.
			if(aSub !== bSub) {
				return aSub < bSub ? -1 : 1;
			}
			// toggle the value of isDigitPart since the parts will alternate.
			isDigitPart = !isDigitPart;
		}
	}

	// else compare strings
	return (a >= b) - (a <= b);
}

/**
 * Sort compare function for strings with numerical values, in descending order.
 * @params a {String}
 * @params b {String}
 * @returns {number}
 */
static compareStringsWithNumbersDescending(a, b) {
	"use strict";
	var sortVal = Dyn.dataUtil.compareStringsWithNumbers( a, b );
	// note: since +0 === -0, is true, we can just reverse the sign to reverse the sort
	return( - sortVal );
}

static compareMixedValues( val1, val2 ) {
    "use strict";
    let type1 = Dyn.dataUtil.getDataType( val1 ),
        type2 = Dyn.dataUtil.getDataType( val2 );

    if( type1 !== type2 || type1==='missing' ) {
        // comparisons of numeric vs string vs undefined(missing value) don't work properly (always returning false)
        // so if types are different we just replace with the type string and compare, to put them in a consistent order
        val1 = type1;
        val2 = type2;
    }
    if (val1 > val2) {
        return 1;
    }
    if (val1 < val2) {
        return -1;
    }
    return 0; //(val1 == val2)
}

/**
 * Compare elements by a.value and b.value (sort strings and numbers ascending)
 * Used for getting sorted index of data values processed by normalizeCaseValueString()
 * @param a {value:{number|string|undefined}}
 * @param b {value:{number|string|undefined}}
 * @returns {number} - usual <0, 0, >0 sorting for ascending priority values
 */
static compareElementsByValue( a, b ) {
    "use strict";

    // compare primary sort values
    let result = Dyn.dataUtil.compareMixedValues( a.value, b.value ),
        done = false;

    // compare secondary sort values if any, if primary sort is equal (result===0)
    for( let n=2; (result===0) && !done; n++ ) {
        let aVal = a['value'+n], // starting with a.value2
            bVal = b['value'+n]; // starting with b.value2
        done = (typeof aVal === 'undefined') && (typeof bVal === 'undefined');
        if( ! done ) {
           result = Dyn.dataUtil.compareMixedValues( aVal, bVal );
        }
    }
    return result; // -1, 0, or 1
}

/** Compare elements by a.value and b.value, to sort in reverse order.
 * See also compareElementsByValue()
 * @param a
 * @param b
 * @returns {number} - <0, 0, >0 sorting for descending priority values
 */
static compareElementsByValueReverse( a, b ) {
    "use strict";
    var sortVal = Dyn.dataUtil.compareElementsByValue( a, b );
    // note: since (+0 === -0) is true, we can just reverse the sign to reverse the sort
    return( - sortVal );
}

/**
 * Sort a case index array using compareElementsByValue()
 * @param sortArray - array with index, value, value2
 * @param caseArray - array to sort
 */
static sortCaseIndices( sortArray, caseArray) {
    Dyn.assert( sortArray.length === caseArray.length, "mismatch in sortCaseIndices");
    sortArray.sort( Dyn.dataUtil.compareElementsByValue );
    sortArray.forEach( (sv,i) => {
        caseArray[i] = sv.index;
    });
};

/**
 * Create an array of the given length filled with the given value.
 * @param length
 * @param value
 * @param caseNo
 * @returns {Array}
 */
static filledArray( length, value, caseNo ) {
	"use strict";
	var a = Array(length).fill(value);
    if(caseNo) {
        a[0] = caseNo;
    }
	//var a = [], i;
	//a.length = length;
	//if( a.length === length ) {
	//	for( i=0; i<length; i++ ) {
	//		a[i] = value;
	//	}
	//}
	return a;
}

/**
 * Create an array of integers 1..length, e.g. indexArray(4) => [1, 2, 3, 4]
 * Use optional index origin to create 0-origin arrays, e.g. indexArray(4, 0) => [0, 1, 2, 3]
 * @param length
 * @param indexOrigin
 * @returns {any[]}
 */
static indexArray( length, indexOrigin ) {
  var a = Array(length).fill(0);
  indexOrigin = isFinite(indexOrigin) ? indexOrigin : 1;
  a.forEach( (v, i, a) => {
      a[i] = indexOrigin + i;
  });
  return a;
}

/**
 * returns a random index from 0 to (length-1)
 * @param length -- positive integer
 * @returns {number} -- random integer from 0 to (length-1)
 */
static randomPick( length ) {
    return Math.floor( Math.random() * length );
}

/**
 * Create an array of {sampleLength} random values from inputArray (randomly picked with replacement)
 * NOTE: with replacement means that we can pick values already selected (duplicates are possible)
 * @param inputArray -- data array
 * @param sampleLength -- length of sample (can be shorter or longer than inputArray)
 * @returns {Array} -- random values of inputArray, with length of sampleLength
 */
static randomPickArray( inputArray, sampleLength ) {
    let inputLength = inputArray.length,
        a = [];
    for( let i=0; i<sampleLength; i++ ) {
        a.push( inputArray[ this.randomPick( inputLength ) ] );
    }
    return a;
}

/**
 * Create an array of {sampleLength} of {totalLength} values of inputArray randomly shuffled (picked without replacement)
 * NOTE: without replacement means that we don't repeat the values already selected (no duplicate indices)
 * @param inputArray -- data array
 * @param sampleLength -- length of sample (can be shorter or longer than inputArray)
 * @returns {Array} -- random values of inputArray, with length of min( sampleLength, inputArray.length )
 */
static randomShuffleArray( inputArray, sampleLength ) {
    var totalLength = inputArray.length,
        a = this.randomShuffleIndexArray( sampleLength, totalLength, 0 );
    return a.map( (element, index, array) => inputArray[element] );
}

/**
 * Create an array of {sampleLength} of {totalLength} integers randomly shuffled (selected without replacement)
 * NOTE: without replacement means that we don't repeat the values already selected (no duplicate indices)
 * randomShuffleIndex(1,4,1) => [1], or [2] or [3], or [4]
 * randomShuffleIndex(2,4,1) => [x, y] of array [1,2,3,4] where x != y and x and y randomly selected
 * randomShuffleIndex(3,1) => [3, 1, 2] or [1, 2, 3], etc.
 * randomShuffleIndex(3,0) => [2, 0, 1], etc.
 * @param sampleLength -- length of array
 * @param totallength -- largest value to sample from
 * @param indexOrigin -- optional index origin, 0 for values 0+, 1 for values 1+ (default)
 * @returns {Array}
 */
static randomShuffleIndexArray( sampleLength, totalLength, indexOrigin ) {
    var a = this.indexArray( totalLength, indexOrigin ); // array 0...(totalLength - 1)
    sampleLength = Dyn.mathUtil.clipToRange( sampleLength, 0, totalLength ); // deal with logic errors
    for( let i=0; i<sampleLength; i++ ) {

        // pick a random case index for the ith position, without replacement
        let casesLeft = totalLength - i,
            randomCaseIndex = i + this.randomPick( casesLeft );
        Dyn.assertArrayIndex( a, randomCaseIndex );

        // swap i and randomCaseIndex (note: it is OK if i===randomCaseIndex)
        [ a[i], a[randomCaseIndex]] = [ a[randomCaseIndex], a[i]];
    }
    return a.slice( 0, sampleLength );
}

/**
 * Clone or deep-copy the given Object or Array or primitive.
 *
 * Note: http://stackoverflow.com/questions/7486085/copying-array-by-value-in-javascript
 * says that $.extend is the most efficient way.
 * Note: we don't handle functions correctly.  For now we just assert that object is not a function.
 *
 * @param {{}|[]} oldObject - Array or Object to copy.
 * @returns {{}|[]} - deep copy of Array or Object
 */
static clone( oldObject ) {
	var isObject = (typeof oldObject === "object"),
		isFunction =( typeof oldObject === "function"),
		isArray = (oldObject instanceof Array),
		isPrimitive =!( isObject || isFunction || isArray ),
		newObject = isArray ? [] : {};
	Dyn.assert( isObject || isArray || isPrimitive );
	if( isPrimitive ) {
		return oldObject; // primitives are always copied by return or assignment.
	} else {
		return $.extend( true, newObject, oldObject );
	}
}

/**
 * Get the checksum of a string.  Also useful for other data values via checksum( n.toString())
 * returns an 32-bit integer checksum encoded as an string containing its hex value
 * http://stackoverflow.com/questions/811195/fast-open-source-checksum-for-small-strings
 * @param s
 * @returns {string}
 */
static checksum(s) {
    "use strict";
    var chk = 0x12345678;
    var len = s.length;
    for (var i = 0; i < len; i++) {
        chk += (s.charCodeAt(i) * (i + 1));
    }
    return (chk & 0xffffffff).toString(16); // eslint-disable-line no-bitwise
}

/**
 * Return the modified string suitable as a string literal in a regular expression.
 * @param str
 * @returns {void|XML|string|*}
 */
static escapeRegExp( literal_string ) {
    "use strict";
    // from http://stackoverflow.com/questions/3561493/is-there-a-regexp-escape-function-in-javascript:
    //  If you also need to future-proof the regular expression against potential additions to the JavaScript regex engine capabilities, I recommend using the more paranoid:
    //  This function escapes every character except those explicitly guaranteed not be used for syntax in future regular expression flavors.
    return literal_string.replace(/[^A-Za-z0-9_]/g, '\\$&');
}

/** Get spreadsheet-like column names 'Case', 'A', 'B', ...'AA', 'AB'...'ZZ'
 * @param columnIndex -- integer 0-(26*26-1)
 * @returns {string}
 */
static defaultColumnName( columnIndex ) {
    const A = 65, n = 26;
    if( columnIndex === 0 ){
        return "Case";
    } else {
        --columnIndex; // skip col 0 which is "Case"
    }
    if( columnIndex < n ) {
        return String.fromCharCode( A + columnIndex % n );
    } else {
        return String.fromCharCode( A + Math.floor(columnIndex/n) - 1, A + columnIndex % n );
    }
}

static defaultAttributeName( columnIndex ) {
    return ( columnIndex === 0 ) ? "Case" : Dyn.loc('attribute{{n}}',{n:columnIndex});
}


    static unitTestDataType( unitTester ) {
        let dataTypeExamples = [
            // value,
            //      expected natural dataType,
            //          optional expected normalizedType (missing|number|string)
            //              optional type to force
            [ 1, "number" ],
            [ -1, "number" ],
            [ 1e4, "number"],
            [ -123.456, "number"],
            [ "", "missing" ],
            [ undefined, "missing" ],
            [ null, "missing" ],
            [ NaN, "missing" ],
            [ {}, "missing"],
            [ ()=>true, "missing"],
            [ true, "string" ],
            [ false, "string"],
            [ "Cat or Dog", "string" ],
            [ "2014-01-02", "number:date", "string" ],
            [ "1900-01", "number:yearMonth", "string" ],
            [ "1900-January", "number:yearMonth", "string" ],
            [ "1900/January", "number:yearMonth", "string" ],
            [ "January 1900", "number:monthYear", "string" ],
            [ "Jan-2000", "number:monthYear", "string" ],
            [ "Jan, 2000", "number:monthYear", "string" ],
            [ "2000", "number:year", "number" ],
            [ "January", "number:month", "string" ],
            [ "Wednesday", "number:dayOfWeek", "string" ],
            [ "1985-May", "number:yearMonth", "string" ],
            [ "1985 May", "number:yearMonth", "string" ],
            [ "1985/May", "number:yearMonth", "string" ],
            [ "1985, May", "number:yearMonth", "string" ],
            [ "1985-05", "number:yearMonth", "string" ],
            [ "1985/05", "number:yearMonth", "string" ],
            [ "1985 05", "string", "string" ], // COUNTER EXAMPLE, because space is not a separator for YYYY-MM format (only if month is a name)
            [ "456,789", "number:extractedNumber", "string" ],
            [ "456,789", "number:extractedNumber", "string", "number:extractedNumber" ],
            [ '01:02.345', "number:durationHM", "string"],
            [ '-01:02.345', "number:durationHM", "string"],
        ];
        testDataTypes( dataTypeExamples );

        function testDataTypes( examples ) {
            examples.forEach( ta => {
                let value = ta[0],
                    expectedDataType = ta[2] || ta[1].split( ':' )[0], // remove subtype
                    expectedFullType = ta[1],
                    forcedType = ta[3] ? ta[3].split( ':' )[0] : null,
                    forcedSubtype = ta[3] ? ta[3].split( ':' )[1] : null;
                let format = {},
                    dataType = Dyn.dataUtil.getDataType( value, format, forcedType, forcedSubtype ),
                    fullType = (format.subtype) ? dataType + ':' + format.subtype : dataType,
                    normalizedValue = Dyn.dataUtil.normalizeCaseValueString( ta[0] ),
                    typeofJS = (normalizedValue === Dyn.dataUtil.kMissingValue) ? "missing" : typeof normalizedValue;

                unitTester.test( fullType === expectedFullType, "Dyn.dataUtil.getDataType() failure, " + value + ' type is ' + fullType + " not " + expectedFullType );
                unitTester.test( typeofJS === expectedDataType, "Dyn.dataUtil.normalizeCaseValueString() failure, " + value + ' type is ' + typeofJS + " not " + expectedFullType );
                if( fullType !== expectedFullType ) {
                    Dyn.dataUtil.stringToDateTime( value, {} ); // check again in debugger for tracing
                }
                if( typeofJS !== expectedDataType ) {
                    Dyn.dataUtil.normalizeCaseValueString( value ); // check again in debugger for tracing
                }
                //console.log( "testDataTypes", typeofJS, ta );
            } );
        }
    }

    static unitTestYearMonthYear( unitTester ) {
        let ymy = [ 'Jan-2000', '2000-Jan', '01-2000', '2000-01' ],
            ymySerial = ymy.map( str => Dyn.dataUtil.stringToDateTime( str ) ),
            ymyFormat1 = ymySerial.map( num => Dyn.dataUtil.monthYearSerialToString( num, 0, 'YYYY-MM' ) ),
            ymyFormat2 = ymySerial.map( num => Dyn.dataUtil.monthYearSerialToString( num, 0, 'Mmm-YYYY' ) );
        unitTester.test( !ymySerial.find( num => (num !== (0 + (12 * 2000))) ), "Found YM or YM not matching target value" );
        unitTester.test( !ymyFormat1.find( str => (str !== '2000-01') ), "Found YM or YM not matching format" );
        unitTester.test( !ymyFormat2.find( str => (str !== 'Jan-2000') ), "Found YM or YM not matching format" );
    }

    static unitTestStringExtractNumber( unitTester ) {
        // test what should be VALID Numerical:ExtractedNumber examples
        [   // string, expected value of extracted number, isForced
            ["2", 2],
            ["2.3", 2.3],
            ["-2.3", -2.3],
            ["-2.3e10 inches", -2.3e10, true],
            ["space -2.3e10", -2.3e10, true],
            ["2/3/4", 2, true],
            ["456,789", 456789],
            ["123.45", 123.45],
            ["Changing M 2.1", 2.1, true ]
        ].forEach( ([s, expectedNum, isForced]) => {
            let n = Dyn.dataUtil.stringExtractNumber( s, null, isForced);
            unitTester.test( Dyn.mathUtil.isFinite( n ) && n===expectedNum, "'"+ s + "' has a valid number extracted from it: "+n );
        });

        // test what should be INVALID Numerical:ExtractedNumber examples
        [
            "", "space", ".", "-", "-.", "", "three"
        ].forEach(( s) => {
            let n = Dyn.dataUtil.stringExtractNumber( s );
            unitTester.test( Dyn.mathUtil.isFinite( n )===false, "'"+ s + "' cannot be converted to a number: "+n );
        });
    }

    static unitTestSortCompare( unitTester ) {
        let testComparisons = [
            ["-4.321 miles", "-3.321 miles", -1], // <
            ["1234 miles", "523 miles", 1], // >
            ["abc3", "abc4", -1], // <
            ["abc3", "abc3", 0], // =
            ["---", "444", -1], // >
            ["", " ", -1], // <
        ].forEach( (test) => {
            unitTester.test( Dyn.dataUtil.compareStringsWithNumbers( test[0], test[1]) === test[2], "compareStringsWithNumbers error: "+test.toString());
        });
    }

    static unitTestDuration( unitTester ) {
        const subtypes = Dyn.dataUtil.subtypes;
        function testDuration( str, subtype, decimals=15, roundedStr="" ) {
            let format = {},
                result = Dyn.dataUtil.stringToDateTime( str, format ),
                resultStr = Dyn.dataUtil.durationSerialToDurationString( format.value, format.subtype, decimals ),
                desiredStr = roundedStr ? roundedStr : str;
            unitTester.test( desiredStr===resultStr && format.subtype === subtype, "error converting duration: "+str+" result: "+resultStr );
        }

        testDuration( "12:32:56", subtypes.kDurationHMS );
        testDuration( "00:00:00", subtypes.kDurationHMS );
        testDuration( "00:00:01", subtypes.kDurationHMS );
        testDuration( "00:02:01", subtypes.kDurationHMS );
        testDuration( "00:02:01.234", this.subtypes.kDurationHMS, 1, "00:02:01.2" );
        testDuration( "-100:02:01", subtypes.kDurationHMS );

        testDuration( "02:01", subtypes.kDurationHM );
        testDuration( "00:01", subtypes.kDurationHM );
        testDuration( "-00:01", subtypes.kDurationHM );
        testDuration( "01:02.345", subtypes.kDurationHM );
        testDuration( "-01:02.345", subtypes.kDurationHM );
    }

    static unitTestFractions( unitTester ) {
        const subtypes = Dyn.dataUtil.subtypes,
            isForced = true;
        // string-in, num-out, format-in, string-out, forced-type :
        testFraction( "1 4/8", 1+4/8 ); // default to simplified proper format
        testFraction( "1 4/8", 1+4/8, 'SP1/2', '1 1/2' ); // simplified proper format
        testFraction( "1 4/8", 1+4/8, 'UP1/8', '1 4/8' ); // unsimplified format
        testFraction( "1 4/8", 1+4/8, 'UI1/8', '12/8' );  // unsimplified+improper format
        testFraction( "1 4/8", 1+4/8, 'SI1/8', '3/2' );   // simplified+improper format
        testFraction( "-1", -1 );
        testFraction( "-1", -1, "SP1/2", "-1" );
        testFraction( "-1", -1, "UP1/2", "-1" );
        testFraction( "-1", -1, "UI1/2", "-2/2" );
        testFraction( "-1", -1, "SI1/2", "-1/1" );
        testFraction( "0", 0 );
        testFraction( "0", 0, "SP1/2", "0" );
        testFraction( "0", 0, "UP1/2", "0" );
        testFraction( "0", 0, "UI1/2", "0/2" );
        testFraction( "0", 0, "SI1/2", "0/1" );
        testFraction( "-0", 0, "", "0" ); // negative zero is always simplified to zero
        testFraction( "1 2/3", 1+2/3 );
        testFraction( "-1 20/30", -1+-20/30, 'SP1/30', '-1 2/3' );
        testFraction( "1/3", 1/3 );
        testFraction( "0.333333333333333333", 1/3, 'SP1/3', '1/3' );
        testFraction( "0.333333", 333333/1000000, 'SP1/3', '333333/1000000' );
        testFraction( "65", 65 );
        testFraction( "-3/4", -3/4 );
        testFraction( "-4 3/30", -4+-3/30, 'SP1/10', '-4 1/10' );
        testFraction( "-4 3/30", -4+-3/30, 'UP1/30', '-4 3/30' );
        testFraction( "-300", -300);
        testFraction( "1/2024", 1/2024, undefined, undefined, isForced );

        function testFraction( str, expectedNum, formatString, expectedStr, forcedSubType = subtypes.kFraction ) {
            let format = {},
                resultNum = fractionData.fractionStringToNumber( str, format, forcedSubType );
            formatString = formatString || format.formatString;
            expectedStr = expectedStr || str;
            let resultStr = Dyn.fractionData.numberToFractionString( format.value, formatString );
            expectedStr = expectedStr || str;
            //console.log( "testFraction", str, expectedNum, formatString, expectedStr, "===>", resultStr, resultNum );
            unitTester.test( resultNum===expectedNum && resultStr===expectedStr && format.subtype === subtypes.kFraction,
                "error converting fraction: "+str+" result: "+resultStr+" (expected: "+expectedStr+") with value:"+resultNum+" expected:"+expectedNum+" format:"+formatString );
        }
    }

    static unitTest() {
        let unitTester = new Dyn.UnitTester( "Dyn.dataUtil.unitTest()" );

        this.unitTestDataType( unitTester );
        this.unitTestYearMonthYear( unitTester );
        this.unitTestStringExtractNumber( unitTester );
        this.unitTestSortCompare( unitTester );
        this.unitTestDuration( unitTester );
        this.unitTestFractions( unitTester );

        unitTester.test( Dyn.dataUtil.checksum("") !== Dyn.dataUtil.checksum(" "), "checksum() error" );
        unitTester.test( Dyn.dataUtil.checksum("1") !== Dyn.dataUtil.checksum("1.0"), "checksum() error");

        unitTester.report();
        return true;
    }

} // class dataUtil
Dyn.dataUtil = dataUtil;

Dyn.dataUtil.kThisSundayDateMS = Dyn.dataUtil.calcThisSundayDateMS();
Dyn.dataUtil.typesArray = Dyn.dataUtil.getTypesArray();
Dyn.dataUtil.subtypesArray = Dyn.dataUtil.getSubtypesArray();
Dyn.debugDataUtil_UnitTestHasRun = false;

setTimeout( () => { // delay the test until localizer Dyn.log() is loaded, for testing Date-Time formatting
    if( Dyn.debugging ) {
        Dyn.debugDataUtil_UnitTestHasRun = Dyn.dataUtil.unitTest();
    }
}, 100 );

// Unit test cases for checking data types and conversions.
(function unitTests(){

	/** test data types and conversions used to take imported values into Dyn.DataSet(), using these conversions:
     * Dyn.dataUtil.normalizeCaseValueString() -- take scalar values and convert into either number, string, or undefined (eliminate boolean, object, null string, etc)
     * Dyn.dataUtil.getDataType() -- get the default data type for a scalar value (and the final value if converted, e.g. string to number, etc)
     * @param finalType - expected final type { "number" | "string | "missing" }
     * @param value - original value
     * @param normalizedValue - expected normalized value (to eliminate boolean, object, null string, etc, not necessary the final value)
     * @param finalValue - optional final, internal value, when normalized value is not the final value, e.g. Date.parse() serial number.
     * @returns {boolean} -- true if finalType, finalValue, and converted Value match results of getDataType() and normalizeCaseValueString()
     */
	function testDataType( finalType, value, normalizedValue, finalValue ) {
		var pass = true,
			caseFormat = {},
			actualNormalizedValue = Dyn.dataUtil.normalizeCaseValueString( value ),
			actualDataType = Dyn.dataUtil.getDataType( value, caseFormat );

		pass = pass && Dyn.assert( actualNormalizedValue === normalizedValue, "error converting value "+value+" to "+normalizedValue+" got:"+actualNormalizedValue );
		pass = pass && Dyn.assert( actualDataType === finalType, "error in final data type for "+value+" want:'"+finalType+"' got:'"+actualDataType+"'" );
		if( finalValue ) {
			pass = pass && Dyn.assert( caseFormat.value === finalValue, "error in final value for "+value+" want:'"+finalValue+"' got:'"+caseFormat.value+"'" );
		}
		return pass;
	}

	function testClone( obj1, obj2 ) {
		var pass = false;
		if( typeof obj === "object" ) {
			for( var i in obj1 ) {
				if( obj1.hasOwnProperty( i )) {
					if( typeof obj1[i] === "object") {
						if( obj1[i] === obj2[i] )
							return false; // clone of object should not equal original (otherwise it is a reference, not a copy)
						else {
							testClone( obj1[i], obj2[i] ); // recursively test members
						}
					} else {
						if( obj1[i] !== obj2[i] ) {
							return false; // clone of primitive should equal original
						}
					}
				}
			}
		}
		return true;
	}

	if (typeof QUnit !== "undefined"){
		// string values
		QUnit.test( "dataUtil: DataType - string value tests", function( assert ) {
			assert.ok( testDataType( "string", "true", "true" ), "string 'true' stays a string data value" );
			assert.ok( testDataType( "string", "false", "false" ), "string 'false' stays a string data value" );
			assert.ok( testDataType( "string", "undefined", "undefined" ), "string 'undefined' stays a string data value" );
			assert.ok( testDataType( "string", "i am a string", "i am a string" ), "string 'i am a string' stays a string data value" );
			assert.ok( testDataType( "string", "I am 2", "I am 2" ), "string 'I am 2' stays a string data value, does not get converted to Date" );
            assert.ok( testDataType( "string", true, "true" ), "boolean true converts to string 'true'" );
            assert.ok( testDataType( "string", false, "false" ), "boolean false converts to string 'false'" );
		});

		// missing values
		QUnit.test( "dataUtil: DataType - missing value tests", function( assert ) {
			assert.ok( testDataType( "missing", undefined, undefined ), "undefined converts to a missing value" );
			assert.ok( testDataType( "missing", null, undefined ), "null converts to a missing value" );
			assert.ok( testDataType( "missing", "", undefined ), "empty string converts to a missing value" );
			assert.ok( testDataType( "missing", "-", undefined ), "string '-' converts to a missing value" );
			assert.ok( testDataType( "missing", {}, undefined ), "object {} converts to a missing value" );
			assert.ok( testDataType( "missing", function() { "testme"; }, undefined ), "function() converts to a missing value" );
		});

		// numeric values
		QUnit.test( "dataUtil: DataType - numeric value tests", function( assert ) {
			assert.ok( testDataType( "number", 1.2, 1.2 ), "(1.2) is a number" );
			assert.ok( testDataType( "number", 0, 0 ), "0 is a number" );
			assert.ok( testDataType( "number", "2015", 2015 ), "string '2015' converts to a number" );
            assert.ok( testDataType( "number", "-1.234", -1.234 ), "test negative numbers" );
            assert.ok( testDataType( "number", "1,234,567.2345", "1,234,567.2345", 1234567.2345 ), "string '1,234,567.2345' converts to number (commas removed)" );
            assert.ok( testDataType( "string", "+1,234,567.2345", "+1,234,567.2345" ), "string '+1,234,567.2345' stays a string (plus not allowed)" );
            assert.ok( testDataType( "string", "1,23,45.67", "1,23,45.67" ), "string '1,23,45.67' stays a string, because number with commas in wrong places treated as a string" );
        });

		// special numeric subtypes: stored as strings, converted to numbers as needed
		QUnit.test( "dataUtil: DataType - special numeric subtypes - stored as strings", function( assert ) {
			/**
			 * Valid ALL-NUMERIC DateTime Formats:
			 * 	YYYY-MM-DD HH:MM:SS AM/PM (e.g. 2015-03-25 04:05:30 PM)
			 * 	YYYY-MM-DD HH:MM:SS  (e.g. 2015-03-25 16:05:30)
			 * 	YYYY-MM-DD
			 * 	YYYY-MM
			 * 	YYYY/MM/DD HH:MM:SS  (e.g. 2015/03/25 16:05:30)
			 * 	YYYY/MM/DD
			 * 	HH:MM:SS
			 * 	HH:MM
			 * 	Note:
			 * 		DD must be two digits '02' not '2'.
			 * 		YYYY must be four digits '2015' not '15'
			 * 		AM or PM may be used (with space before, e.g. '10:00 AM')
			 *
			 * Valid Mixed Name/Numeric DateTime Formats:
			 *  NOTE:
			 *  	MMM means at least 3 letters of month name, e.g. January, Jan, etc.
			 *      WWW means at least 3 letters of day of week, Monday, Mon, etc.
			 *      DD here means "1" or "01" will work in mixed format.
			 *  WWW MMM DD YYY HH:MM:SS  (e.g. "Wed Mar 25 2015 09:56:24")
			 *  WWW MMM DD YYY
			 *  MMM DD YYYY
			 *  MMM   (Month only, e.g. 'January' or 'Jan')
			 *  WWW   (Day of week only, e.g. 'Monday' or 'Mon')
			 *
			 * 	NOTES:
			 * 	1. invalid dates or times will give unpredictable results, e.g. Feb 31st, "2015/2/31", or "12:99:03" will plot incorrectly.
			 * 	2. Bare years or days will be treated like the ordinary numbers that they are (e.g. year '2000' or day '5').
			 */
			assert.ok( testDataType( "number", "2015-01-02", "2015-01-02", 1420192800000-Dyn.dataUtil.kDateTimeZoneCorrection ), "2015-01-02 converts to a numeric datetime type" );
			assert.ok( testDataType( "number", "2015/03/25 12:00:00", "2015/03/25 12:00:00", 1427317200000-Dyn.dataUtil.kDateTimeZoneCorrection ), "2015/03/25 12:00:00  converts to a numeric datetime type" );
			assert.ok( testDataType( "number", "Mar 25 2015", "Mar 25 2015", 1427274000000-Dyn.dataUtil.kDateTimeZoneCorrection ), "Mar 25 2015 converts to a numeric datetime type" );
			assert.ok( testDataType( "string", "2015 Mar 25", "2015 Mar 25" ), "2015 Mar 25 is not a date, even in Chrome (does not work in Safari or FireFox)" );
			assert.ok( testDataType( "number", "January 25 2015", "January 25 2015" ), "January 25 2015 converts to a numeric datetime type" );
			assert.ok( testDataType( "string", "2015, JANUARY, 25", "2015, JANUARY, 25" ), "2015, JANUARY, 25 is not a date, even in Chrome (does not work in Safari or FireFox)" );
			assert.ok( testDataType( "number", "03/25/2015", "03/25/2015", 1427274000000-Dyn.dataUtil.kDateTimeZoneCorrection ), "03/25/2015 converts to a numeric datetime type" );
			assert.ok( testDataType( "string", "03-25-2015", "03-25-2015" ), "03-25-2015 is not a date, even in Chrome (does not work in Safari or FireFox)" );
			assert.ok( testDataType( "number", "2015/03/25", "2015/03/25", 1427274000000-Dyn.dataUtil.kDateTimeZoneCorrection ), "2015/03/25 converts to a numeric datetime type" );
			assert.ok( testDataType( "number",  "Wed Mar 25 2015 09:56:24 GMT+0100 (W. Europe Standard Time)",
									            "Wed Mar 25 2015 09:56:24 GMT+0100 (W. Europe Standard Time)",
									            1427273784000 ), // no time zone correction because time zone is specified, parse() does it for us
									            "Wed Mar 25 2015 09:56:24 GMT+0100 (W. Europe Standard Time) converts to a numeric datetime type" );
			assert.ok( testDataType( "number",  "Fri Mar 25 2015 09:56:24 GMT+0100 (Tokyo Time)",
									            "Fri Mar 25 2015 09:56:24 GMT+0100 (Tokyo Time)",
									            1427273784000 ), // no time zone correction because time zone is specified, parse() does it for us
									            "Fri Mar 25 2015 09:56:24 GMT+0100 (Tokyo Time) converts to a numeric datetime type" );
			assert.ok( testDataType( "string", "March 2015", "March 2015" ), "'March 2015' is not a date, even in Chrome (does not work in Safari or FireFox)");
			assert.ok( testDataType( "string", "12 (12+ 1960-1990)", "12 (12+ 1960-1990)" ), "'12 (12+ 1960-1990)' is not a date, even in Chrome (does not work in Safari or FireFox)");
            assert.ok( testDataType( "string", "78%", "78%"), "'78%' is a string, not a date, even in Chrome which will convert to date using Date.parse()");
		});

		// relative dates and times: internally converted to a datetime based on today's date, so the value varies for each test instantiation.
		QUnit.test( "dataUtil: DataType - relative time and date value tests", function( assert ) {
			var today12_30_06_MS = Date.parse( Dyn.dataUtil.kTodaysDateString) + ((((12*60)+30)*60+6)*1000),
				today12_30_MS = Date.parse( Dyn.dataUtil.kTodaysDateString) + ((((12*60)+30)*60+0)*1000),
				thursdayMS = Dyn.dataUtil.kThisSundayDateMS + (Dyn.dataUtil.kDayInMS * Dyn.dataUtil.kDaysOfWeekShortLC.indexOf("thu")),
				month0 = -1; // convert from 1-12 to 0-11 for months, index origin is zero
				//marchThisYearMS = Date.parse( Dyn.dataUtil.kThisYearString + " " + "March" );

			assert.ok( testDataType( "number", "12:30:06", "12:30:06", today12_30_06_MS ), "12:30:06  converts to a numeric datetime type" );
			assert.ok( testDataType( "number", "12:30", "12:30", today12_30_MS ), "12:30:00  converts to a numeric datetime type" );

			assert.ok( testDataType( "number", "sunday", "sunday", Dyn.dataUtil.kThisSundayDateMS ), "sunday converts to a numeric datetime type" );
			assert.ok( testDataType( "number", "SUNDAY", "SUNDAY", Dyn.dataUtil.kThisSundayDateMS ), "SUNDAY converts to a numeric datetime type" );
			assert.ok( testDataType( "number", "Thursday", "Thursday", thursdayMS ), "Thursday converts to a numeric datetime type" );
			assert.ok( testDataType( "string", "Thurs.", "Thurs." ), "'Thurs.' is a string type because of the period" );

			assert.ok( testDataType( "number", "march", "march", month0 + 3  ), "march  converts to a numeric datetime type" );
			assert.ok( testDataType( "number", "March", "March", month0 + 3 ), "March  converts to a numeric datetime type" );
			assert.ok( testDataType( "number", "Mar", "Mar", month0 + 3 ), "'Mar' converts to a numeric datetime type" );
			assert.ok( testDataType( "string", "Married", "Married" ), "'Married' is a string type" );
			assert.ok( testDataType( "number", "Sep", "Sep", month0 + 9 ), "'Sep' converts to a numeric datetime type" );
			assert.ok( testDataType( "string", "Sep/Div", "Sep/Div" ), "'Sep/Div' is a string type" );
		});

		QUnit.test( "dataUtil: clone() -- deep copy of various object types", function( assert ) {
			var a1 = [1, 2, 3, [4, "five", 6], undefined, null, { hi:"there"}],
				o1 = { param1:"hi", param2: [ {}, 2 ] };

			assert.ok( testClone( a1, "clone creates a deep copy of array" ));
			assert.ok( testClone( o1, "clone creates a deep copy of object" ));
			assert.ok( testClone( 47, "clone creates an ordinary copy of number" ));
			assert.ok( testClone( 0, "clone creates an ordinary copy of number" ));
			assert.ok( testClone( true, "clone creates an ordinary copy of boolean" ));
			assert.ok( testClone( false, "clone creates an ordinary copy of boolean" ));
			assert.ok( testClone( "my string", "clone creates an ordinary copy of string" ));
			assert.ok( testClone( "", "clone creates an ordinary copy of string" ));
		});


		// QUnit.test( "dataUtil: test stringExtractNumber()", function( assert ) {
		// 	var validNumbers = ["2", 2, "2.3", 2.3, "-2.3", -2.3, "-2.3e10 inches", -2.3e10,  "space -2.3e10", -2.3e10, "2/3/4", 2 ],
		// 		notValidNumbers = ["", "space", ".", "-", "-.", "", "three"],
		// 		i, j, s, n;
        //
		// 	for( i=0, j=validNumbers.length; i<j; i+=2 ) {
		// 		s = validNumbers[i];
		// 		n = Dyn.dataUtil.stringExtractNumber( s );
		// 		assert.ok( Dyn.mathUtil.isFinite( n ) && n===validNumbers[i+1], "'"+ s + "' has a valid number extracted from it: "+n );
		// 	}
		// 	for( i=0, j=notValidNumbers.length; i<j; i+=2 ) {
		// 		s = notValidNumbers[i];
		// 		n = Dyn.dataUtil.stringExtractNumber( s );
		// 		assert.ok( Dyn.mathUtil.isFinite( n )===false, "'"+ s + "' cannot be converted to a number: "+n );
		// 	}
		// });

		// QUnit.test( "dataUtil: test functions", function( assert ) {
		//     assert.ok( Dyn.dataUtil.checksum("") !== Dyn.dataUtil.checksum(" "));
        //     assert.ok( Dyn.dataUtil.checksum("1") !== Dyn.dataUtil.checksum("1.0"));
        // });

        // QUnit.test( "dataUtil: test replaceAll( string, find, replace )", function( assert ) {
        //     assert.ok( "dot.dash=slash\two=questions??now" === Dyn.dataUtil.replaceAll( "dot.dash-slash\two-questions??now", "-", "="), "dash to equals twice" );
        //     assert.ok( "if( my;oh_my / my;oh_my + my;oh_my" === Dyn.dataUtil.replaceAll( "if( my;oh[my / my;oh[my + my;oh[my", "oh[", "oh_"), "oh[ to oh_ thrice" );
        // });
	}
})();

/** construct an attribute stats. Numeric, categorical, and missing-value statistics */
class AttributeStats {
    constructor() {
        this.missingCaseCount = 0;
        this.numericCaseCount = 0;
        this.categoricCaseCount = 0;
        this.subtypeCounts = {};
        this.formatCounts = {};
        this.categoricIndvCaseCount = {};
        this.min = Dyn.mathUtil.MAX_SAFE_INTEGER; //Number.MAX_VALUE;
        this.max = Dyn.mathUtil.MIN_SAFE_INTEGER; //Number.MIN_VALUE;
        this.categories = [];	// set of unique categorical values
        this.numericDecimals = 0;	// maximum decimal points in the numeric value, 0+
        this.filterID = 0;	// ID for unique filter configuration used to calculate stats
        this.hasTrailingZeros = false;   // trailing zeros were found in the numbers
    }

    isCategorical() {
        // true if there are categorical values, or a mix of categorical, numeric, or missing values,
        // or only numeric values, but rejected for format inconsistencies
        return( this.categoricCaseCount > 0 || (this.numericCaseCount > 0 && !this.isNumeric()));
    }
    isNumeric() {
        // true if 90% or more of non-missing cases are numeric
        // so that a small number of categorical values are treated as exceptions, and all missing values are exceptions
        if( this._is90PctOfNonMissingCases( this.numericCaseCount )) {
            // but reject if we have extracted numbers with inconsistent formatting, e.g. "$50" and "50%"
            let hasExtractedNumbers = this.subtypeCounts[ Dyn.dataUtil.subtypes.kExtractedNumber ],
                hasMultipleFormats = this.countFormatStrings() >= 2;
            return !( hasExtractedNumbers && hasMultipleFormats);
        }
        return false;
    }
    isMissing() {
        // true if no numeric or categorical case values
        return( this.numericCaseCount === 0 && this.categoricCaseCount === 0 );
    }
    numCategories() {
        return this.categories.length;
    }
    _is90PctOfNonMissingCases( caseCount ) {
        // is the count of cases at least 90% of non-missing cases for this attribute?
        // used to eliminate many false positives when finding the default data type
        return( caseCount > 0 && caseCount >= (0.90 * (this.numericCaseCount + this.categoricCaseCount )));
    }

    /** return most popular numeric subtype string or undefined if no matching subtypes */
    getNumericSubtype() {
        let stats = this;
        function isNonZero( caseCount ) {
            return( caseCount > 0 );
        }
        function countOf( t ) {
            return stats.subtypeCounts[ t ] || 0; // e.g. 8 cases with type 'time'
        }

        // first check to see if numeric; note that if cases with numeric subtypes exist, but they are not consistently
        // formatted, isNumeric() will return false, so they are treated as categorical data.
        if( this.isNumeric()) {
            let subtypes = Dyn.dataUtil.subtypes;

            // check date/time and duration combinations first
            if( isNonZero( countOf( subtypes.kDateTime )))
                return subtypes.kDateTime;      // is dateTime if at least one value with date and time
            if( isNonZero( countOf( subtypes.kDate )) && isNonZero( countOf( subtypes.kTime ))
                && this._is90PctOfNonMissingCases( countOf( subtypes.kDate )+countOf( subtypes.kTime )))
                return subtypes.kDateTime;      // mix of date-only and time-only values
            if( isNonZero( countOf( subtypes.kDurationHMS )) && isNonZero( countOf( subtypes.kDurationHM ))
                && this._is90PctOfNonMissingCases( countOf( subtypes.kDurationHMS )+countOf( subtypes.kDurationHM ))) {
                return subtypes.kDurationHMS;   // mix of 2 and 3 part duration types, use 3 parts
            }
            // check all other subtypes
            for( let t in subtypes ) {
                if( subtypes.hasOwnProperty( t )
                    && this._is90PctOfNonMissingCases( countOf( subtypes[t] )))
                    return subtypes[t];     // 90%+ of cases are the special type (month/year/date/time)
            }
            if( isNonZero( countOf( subtypes.kFraction )))
                return subtypes.kFraction;
            // check for extracted number last, because dates look like an extracted number (string starting with digits)
            if( isNonZero( countOf( subtypes.kExtractedNumber )))
                return subtypes.kExtractedNumber;
        }
        return; // undefined = no subtype;
    }

    /** merge in another stats object, as if the cases were combined */
    mergeStats( other ) {
        function mergeTypeCounts( thisCounts, otherCounts ) {
            for( var subtypeKey in otherCounts ) {
                if( otherCounts.hasOwnProperty( subtypeKey ) ) {
                    if( thisCounts.hasOwnProperty( subtypeKey ) ) {
                        thisCounts[subtypeKey] += otherCounts[subtypeKey];
                    } else {
                        thisCounts[subtypeKey] = otherCounts[subtypeKey];
                    }
                }
            }
        }
        function mergeCategories( thisCategories, otherCategories ) {
            otherCategories.forEach( value => {
                if( ! thisCategories.includes( value )) {
                    thisCategories.push( value );
                }
            });
        }

        this.missingCaseCount	+= other.missingCaseCount;
        this.numericCaseCount	+= other.numericCaseCount;
        this.categoricCaseCount	+= other.categoricCaseCount;
        mergeTypeCounts( this.subtypeCounts, other.subtypeCounts );
        mergeTypeCounts( this.formatCounts, other.formatCounts );
        this.min				= Math.min( this.min, other.min );
        this.max				= Math.max( this.max, other.max );
        mergeCategories( this.categories, other.categories );
        this.numericDecimals	= Math.max( this.numericDecimals, other.numericDecimals );
        this.filterID			= Math.min( this.filterID, other.filterID );
    }

    getCategoricIndvCaseCount(caseName) {
        return this.categoricIndvCaseCount[caseName] || 0;
    }

    /**
     * Get the best format string for all of our data values
     * @param subType
     * @returns {string} - "YYYY-MM", else ""
     */
    getFormatString( subType ) {
        switch( subType ) {
            case Dyn.dataUtil.subtypes.kFraction:
                return fractionData.getBestFormatString( this.formatCounts, this.numericDecimals );
            default:
                return mostPopularFormatString( this.formatCounts );
        }
        // get the most popular format string, if any, e.g. "YYYY-MM", else ""
        function mostPopularFormatString( formatCounts ) {
            let maxFormatStr = "", maxCount = 0;
            for( const [ formatStr, count ] of Object.entries( formatCounts ) ) {
                if( maxCount < count ) {
                    maxFormatStr = formatStr;
                    maxCount = count;
                }
            }
            //console.log( "AttributeStats.mostPopularFormatString() mostPopular=", maxFormatStr, formatCounts );
            return maxFormatStr;
        }
    }

    /**
     * count the number of different format strings captured by the cases of our attribute,
     * e.g. 20 cases of "${{n}}", 3 cases of "{{n}}%", return 2
     */
    countFormatStrings() {
        //console.log( "AttributeStats.countFormatStrings()", Object.keys( this.formatCounts ));
        return Object.keys( this.formatCounts ).length;
    }

    /**
     * Update our stats to use ordered numeric categories, as formatted by the attribute.
     * @param categories - array of strings
     * @param categoryToValueMap - object with category to numeric value map e.g. {'$10.3':10.3}
     */
    setOrderedNumericCategories( categories, categoryToValueMap, ) {
        this.categories = categories; // save ordered categories
        this.categoricValueMap = categoryToValueMap;
    }
}   // class AttributeStats
Dyn.dataUtil.AttributeStats = AttributeStats;

} // wrapDyndataUtil
